import { useState, useEffect, useRef } from "react";
import { OrderSource } from "@/components/OrderSource";
import { BlacklistDialog } from "@/components/BlacklistDialog";
import { useAuth } from "@/auth/AuthContext";
import { ShopifyLockerPicker } from "@/components/orders/ShopifyLockerPicker";
import axios from "axios";
import {
  X, Package, User, FileText, Truck, MapPin, ShoppingBag,
  Ban, FilePlus, Wrench, Shield, ExternalLink, AlertCircle, CheckCircle2,
  RefreshCw, RotateCcw, Trash2, MapPinned, Link2, Save, Upload, TrendingUp,
  History, Mail, FileCheck2, Receipt, PackageX, ArrowRightLeft, Clock,
} from "lucide-react";
import AwbGeneratorModal from "./AwbGeneratorModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABS = [
  { id: "info", label: "Informații comandă", icon: Package },
  { id: "customer", label: "Informații client", icon: User },
  { id: "profit", label: "Profit", icon: TrendingUp },
  { id: "docs", label: "Facturi, Garanții & Documente", icon: FileText },
  { id: "awb", label: "AWB-uri", icon: Truck },
  { id: "billing", label: "Adresă facturare", icon: MapPin },
  { id: "shipping", label: "Informații livrare", icon: ShoppingBag },
  { id: "history", label: "Istoric", icon: History },
];

const STATUS_COLORS = {
  "Nouă": "bg-amber-500/10 text-amber-300 border-amber-500/30",
  "În procesare": "bg-blue-500/10 text-blue-300 border-blue-500/30",
  "Pregătită": "bg-violet-500/10 text-violet-300 border-violet-500/30",
  "Finalizată": "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  "Anulată": "bg-rose-500/10 text-rose-300 border-rose-500/30",
  "Returnată": "bg-orange-500/10 text-orange-300 border-orange-500/30",
};

const PLATFORM_LABEL = {
  emag_ro_fbe: "EMAG-RO-FBE", emag_ro_bau: "EMAG-RO-BAU",
  emag_bg_fbe: "EMAG-BG-FBE", emag_bg_bau: "EMAG-BG-BAU",
  emag_hu_fbe: "EMAG-HU-FBE", emag_hu_bau: "EMAG-HU-BAU",
  gomag_bragus: "BRAGUS.ro (Gomag)",
  shopify_bragus: "BRAGUS.ro (Shopify)",
  trendyol_ro: "TRENDYOL-RO", trendyol_bg: "TRENDYOL-BG",
  manual_bragus: "COMANDĂ MANUALĂ",
};

function fmtMoney(value, currency) {
  if (value == null || isNaN(value)) return "—";
  const c = (currency || "RON").toUpperCase();
  if (c === "HUF") return `${Math.round(value).toLocaleString("hu-HU")} HUF`;
  if (c === "EUR") return `€${Number(value).toFixed(2)}`;
  return `${Number(value).toFixed(2)} RON`;
}

function Field({ label, value, testId, highlight }) {
  // `highlight="rose"` switches the card to a red theme — used for the
  // eMAG cancellation reason so it stands out in the info grid.
  const cls = highlight === "rose"
    ? "flex flex-col gap-0.5 rounded-lg border border-rose-500/40 bg-rose-500/10 p-2.5 sm:col-span-2"
    : "flex flex-col gap-0.5 rounded-lg border border-zinc-800 bg-zinc-900/40 p-2.5";
  const labelCls = highlight === "rose"
    ? "text-[9px] font-medium uppercase tracking-wider text-rose-300"
    : "text-[9px] font-medium uppercase tracking-wider text-zinc-500";
  const valueCls = highlight === "rose"
    ? "text-sm font-semibold text-rose-100 break-words"
    : "text-sm text-zinc-100 break-words";
  return (
    <div data-testid={testId} className={cls}>
      <span className={labelCls}>{label}</span>
      <span className={valueCls}>{value || <span className="text-zinc-600">—</span>}</span>
    </div>
  );
}

function ProductThumb({ src, alt }) {
  const url = src ? (src.startsWith("/api/") ? `${BACKEND_URL}${src}` : src) : null;
  return (
    <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-md border border-zinc-800 bg-zinc-900">
      {url ? <img src={url} alt={alt || ""} className="h-full w-full object-cover" loading="lazy" />
        : <div className="flex h-full w-full items-center justify-center text-zinc-700"><Package className="h-5 w-5" /></div>}
    </div>
  );
}

function findAttachment(order, type) {
  const att = order?.attachments || [];
  return att.filter((a) => Number(a.type) === type && a.url);
}

function TabBtn({ tab, active, onClick }) {
  const Icon = tab.icon;
  return (
    <button data-testid={`tab-${tab.id}`} onClick={onClick}
      className={`flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-xs font-medium transition ${active ? "bg-orange-500/15 text-orange-200 ring-1 ring-orange-500/40" : "text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-200"}`}>
      <Icon className="h-3.5 w-3.5" />
      {tab.label}
    </button>
  );
}

// Manual status editor — only rendered for `manual_bragus` / `gomag_bragus`
// orders. Calls PATCH /api/orders/manual/{id}/status which is guarded server
// side to those two integration keys.
function ManualStatusChanger({ order, onChanged }) {
  const STATUSES = [
    { value: 1, label: "Nouă" },
    { value: 2, label: "În procesare" },
    { value: 3, label: "Pregătită" },
    { value: 4, label: "Finalizată" },
    { value: 5, label: "Returnată" },
    { value: 19, label: "Neridicata" },
    { value: 0, label: "Anulată" },
  ];
  const [saving, setSaving] = useState(false);
  // Local status could be `5 + is_unpicked=true` → interpret as 19 in the picker.
  const initialValue = (order.is_unpicked === true || order.status_key === "unpicked")
    ? 19
    : (order.status ?? "");
  const [selected, setSelected] = useState(initialValue);
  const onSave = async () => {
    if (selected === "" || Number(selected) === Number(initialValue)) return;
    const targetLabel = STATUSES.find((s) => s.value === Number(selected))?.label;
    if (!window.confirm(`Confirmi trecerea comenzii la status "${targetLabel}"?`)) return;
    // Ask second question: fire the automation flow or skip it for this
    // status transition only. User request 2026-08-31 — every manual
    // status change on Gomag Bragus / Manual Bragus prompts before firing
    // automations (WhatsApp/Email/notifications) configured for the
    // matching `order_status_changed` trigger.
    const wantsNotification = window.confirm(
      `Trimit notificarea automată setată pentru "${targetLabel}"?\n\n` +
      `OK  = da, urmează flow-ul din Automatizări (email/WhatsApp/etc)\n` +
      `Anulează = nu, skip la automatizări doar pentru schimbarea asta`
    );
    setSaving(true);
    try {
      const oid = order._composite_id || String(order._emag_id || order.id);
      const params = order._integration_key ? { integration_key: order._integration_key } : {};
      await axios.patch(`${API}/orders/manual/${encodeURIComponent(oid)}/status`,
        { status: Number(selected), send_notification: wantsNotification }, { params });
      if (onChanged) await onChanged();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Nu am putut schimba statusul.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div data-testid="manual-status-changer" className="mt-2 flex items-center gap-1.5">
      <select data-testid="manual-status-select"
        value={selected} disabled={saving}
        onChange={(e) => setSelected(e.target.value === "" ? "" : Number(e.target.value))}
        className="flex-1 rounded-md border border-zinc-800 bg-zinc-950 px-1.5 py-1 text-[11px] text-zinc-200 outline-none focus:border-orange-500/50">
        {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
      </select>
      <button data-testid="manual-status-save" onClick={onSave} disabled={saving || Number(selected) === Number(order.status)}
        className="rounded-md border border-orange-500/40 bg-orange-500/10 px-2 py-1 text-[10px] font-medium text-orange-300 hover:bg-orange-500/20 disabled:opacity-40">
        {saving ? "..." : "Schimbă"}
      </button>
    </div>
  );
}

// Payment mode changer — corrects the online↔ramburs mismatch (customer
// picks the wrong option at checkout). For Gomag orders it also pushes the
// change back to Gomag (unpaid/paid status) so Gomag stops the auto
// "unpaid reminder" emails.
function PaymentModeChanger({ order, onChanged }) {
  // Derive current mode from the order — payment_mode_id 3 = online, 1 = ramburs.
  const currentMode = order.payment_mode_id === 3 ? "online"
                    : order.payment_mode_id === 1 ? "ramburs"
                    : ((order.payment_mode || "").toLowerCase().includes("ramburs") ? "ramburs" : "online");
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState(currentMode);
  const [paid, setPaid] = useState(!!order.online_paid);

  const dirty = mode !== currentMode || (mode === "online" && paid !== !!order.online_paid);

  const onSave = async () => {
    if (!dirty || saving) return;
    const confirmMsg = mode === "ramburs"
      ? "Trec comanda pe RAMBURS și opresc mesajele automate de neplată. Continui?"
      : paid
        ? "Marchez online PLĂTITĂ și opresc mesajele automate de neplată. Continui?"
        : "Trec comanda pe ONLINE NEPLĂTITĂ (Gomag va relua reminderele). Continui?";
    if (!window.confirm(confirmMsg)) return;
    setSaving(true);
    try {
      const oid = order._composite_id || String(order._emag_id || order.id);
      const params = order._integration_key ? { integration_key: order._integration_key } : {};
      const r = await axios.patch(`${API}/orders/manual/${encodeURIComponent(oid)}/payment`,
        { payment_mode: mode, paid }, { params });
      const g = r.data?.gomag_sync;
      if (g?.attempted && g?.error) {
        window.alert(`Salvat local, dar sync Gomag a eșuat: ${g.error}\nApasă din nou după ce Gomag e disponibil.`);
      }
      if (onChanged) await onChanged();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Nu am putut schimba metoda de plată.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="manual-payment-changer" className="mt-2 flex flex-col gap-1.5">
      <div className="flex gap-1">
        {[
          { v: "online", label: "Online" },
          { v: "ramburs", label: "Ramburs" },
        ].map((opt) => (
          <button
            key={opt.v}
            type="button"
            data-testid={`payment-mode-${opt.v}`}
            onClick={() => setMode(opt.v)}
            disabled={saving}
            className={`flex-1 rounded-md border px-2 py-1 text-[11px] font-medium transition ${
              mode === opt.v
                ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-200"
                : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
      {mode === "online" && (
        <label className="flex items-center gap-1.5 text-[11px] text-zinc-400">
          <input type="checkbox" checked={paid} onChange={(e) => setPaid(e.target.checked)}
            data-testid="payment-mode-online-paid"
            className="h-3 w-3 rounded border-zinc-700 bg-zinc-900 text-emerald-500" />
          Plătită online (oprește mesajele automate)
        </label>
      )}
      <button
        data-testid="manual-payment-save"
        onClick={onSave}
        disabled={!dirty || saving}
        className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[10px] font-medium text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-40"
      >
        {saving ? "Salvez..." : (dirty ? "Salvează + sync Gomag" : "—")}
      </button>
    </div>
  );
}

function InfoTab({ order, onChanged }) {
  const platform = PLATFORM_LABEL[order._integration_key] || order._integration_key;
  const statusCls = STATUS_COLORS[order.status_label] || "bg-zinc-500/10 text-zinc-300 border-zinc-500/30";
  const paymentLabel = order.payment_mode_id === 3 ? "Online" : order.payment_mode_id === 1 ? "Ramburs" : (order.payment_mode || "—");
  // For Bragus Gomag + Manual Bragus COD orders, append refund status
  const paymentValue = (() => {
    if (order.payment_mode_id !== 1) return paymentLabel;
    if (order._integration_key !== "gomag_bragus" && order._integration_key !== "manual_bragus" && order._integration_key !== "shopify_bragus") return paymentLabel;
    if (order.cod_refund_status === "paid") {
      const dateStr = order.cod_refund_at ? new Date(order.cod_refund_at).toLocaleDateString("ro-RO") : "";
      return (
        <span data-testid="info-payment-refund">
          Ramburs: <span className="font-medium text-emerald-400">Încasată</span>
          {dateStr && <span className="ml-1 text-[10px] text-zinc-500">({dateStr})</span>}
        </span>
      );
    }
    return (
      <span data-testid="info-payment-refund">
        Ramburs: <span className="font-medium text-rose-400">Neîncasată</span>
      </span>
    );
  })();
  const deliveryLabel = (() => {
    const m = (order.delivery_mode || "").toLowerCase();
    if (m === "pickup" || m === "easybox" || m.includes("locker")) return "Locker";
    if (m === "courier" || m === "home" || m.includes("domic")) return "Domiciliu";
    return order.delivery_mode || "—";
  })();
  const [d, t] = (order.date || "").split(" ");
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
      <div data-testid="info-status" className="flex flex-col gap-0.5 rounded-lg border border-zinc-800 bg-zinc-900/40 p-2.5">
        <span className="text-[9px] font-medium uppercase tracking-wider text-zinc-500">Status</span>
        <span className={`mt-0.5 inline-flex w-fit items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium ${statusCls}`}>
          <span className="h-1.5 w-1.5 rounded-full bg-current" />{order.status_label}
        </span>
        {(order._integration_key === "manual_bragus" || order._integration_key === "gomag_bragus" || order._integration_key === "shopify_bragus") && (
          <ManualStatusChanger order={order} onChanged={onChanged} />
        )}
      </div>
      <div data-testid="info-platform" className="flex flex-col gap-0.5 rounded-lg border border-zinc-800 bg-zinc-900/40 p-2.5">
        <span className="text-[9px] font-medium uppercase tracking-wider text-zinc-500">Platformă</span>
        <span className="mt-0.5 text-[13px] text-zinc-100">{platform}</span>
        <OrderSource src={order.shopify_source} testId="info-order-source" />
      </div>
      <Field testId="info-date" label="Data comenzii" value={d ? `${d}  ·  ${t || ""}` : "—"} />
      <div data-testid="info-payment" className="flex flex-col gap-0.5 rounded-lg border border-zinc-800 bg-zinc-900/40 p-2.5">
        <span className="text-[9px] font-medium uppercase tracking-wider text-zinc-500">Metodă plată</span>
        <span className="mt-0.5 text-[13px] text-zinc-100">{paymentValue}</span>
        {(order._integration_key === "manual_bragus" || order._integration_key === "gomag_bragus" || order._integration_key === "shopify_bragus") && (
          <PaymentModeChanger order={order} onChanged={onChanged} />
        )}
      </div>
      <Field testId="info-delivery" label="Metodă livrare" value={deliveryLabel} />
      <Field testId="info-currency" label="Monedă" value={order.currency || "RON"} />
      <Field testId="info-total" label="Total comandă" value={fmtMoney(order.computed_total, order.currency)} />
      {order.finalization_date && <Field testId="info-finalization" label="Data finalizare" value={order.finalization_date} />}
      {order.observation && <Field testId="info-observation" label="Observații" value={order.observation} />}
      {(() => {
        // eMAG returns `reason_cancellation: { id, name }` on cancelled orders.
        // Surface it as a full-width red banner above the rest so the operator
        // immediately sees WHY the order was cancelled.
        const rc = order.reason_cancellation;
        const text = rc && typeof rc === "object"
          ? `${rc.name || ""}${rc.id ? ` (#${rc.id})` : ""}`.trim()
          : (typeof rc === "string" ? rc : null);
        if (!text) return null;
        return (
          <Field testId="info-cancel-reason" label="Motiv anulare eMAG" value={text} highlight="rose" />
        );
      })()}
      {(() => {
        // Manual „Motiv anulare" editabil pentru comenzi Gomag / Manual BRAGUS.
        // Vizibil doar când comanda e Anulată sau Returnată. User request 2026-08-21.
        const integ = (order._integration_key || "").toLowerCase();
        const isGomagOrManual = integ.startsWith("gomag") || integ.startsWith("manual") || integ.startsWith("shopify");
        const isCancelled = [0, 5, 7].includes(Number(order.status));
        if (!isGomagOrManual || !isCancelled) return null;
        return <CancelReasonField order={order} onSaved={onChanged} />;
      })()}
    </div>
  );
}

function CancelReasonField({ order, onSaved }) {
  const [text, setText] = useState(order.cancellation_reason_text || "");
  const [busy, setBusy] = useState(false);
  const initialRef = useRef(order.cancellation_reason_text || "");
  useEffect(() => {
    setText(order.cancellation_reason_text || "");
    initialRef.current = order.cancellation_reason_text || "";
  }, [order.id, order._integration_key, order.cancellation_reason_text]);
  const changed = text !== initialRef.current;
  const save = async () => {
    setBusy(true);
    try {
      const integ = order._integration_key || "";
      const oid = order._emag_id || order.id;
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/${encodeURIComponent(oid)}/cancel-reason${integ ? `?integration_key=${encodeURIComponent(integ)}` : ""}`,
        { reason_text: text.trim() },
      );
      initialRef.current = text.trim();
      onSaved?.();
    } catch (e) {
      window.alert("Eroare la salvare: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };
  return (
    <div data-testid="cancel-reason-editable" className="col-span-full flex flex-col gap-1.5 rounded-lg border border-rose-500/30 bg-rose-500/5 p-2.5">
      <div className="flex items-center justify-between">
        <span className="text-[9px] font-medium uppercase tracking-wider text-rose-300">Motiv anulare (editabil)</span>
        {order.cancellation_reason_updated_at && (
          <span className="text-[9px] text-zinc-500">
            Ultima modificare: {new Date(order.cancellation_reason_updated_at).toLocaleString("ro-RO")}
          </span>
        )}
      </div>
      <div className="flex gap-2">
        <textarea
          data-testid="cancel-reason-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Adaugă motivul anulării (va apărea în raportul lunar și în notificarea clientului)…"
          rows={2}
          className="flex-1 resize-none rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-xs text-zinc-100 placeholder:text-zinc-600 focus:border-rose-500/50 focus:outline-none"
        />
        <button
          data-testid="cancel-reason-save"
          onClick={save}
          disabled={busy || !changed}
          className="self-start inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1.5 text-[11px] font-medium text-emerald-200 disabled:opacity-40 hover:bg-emerald-500/20"
        >
          {busy ? "…" : "Salvează"}
        </button>
      </div>
    </div>
  );
}

function CustomerTab({ order, onBlacklist, blacklisting, insight, isOwner }) {
  const c = order.customer || {};
  const bl = insight ? insight.blacklist : order.blacklist;
  const isLegal = Number(c.legal_entity) === 1;
  const legalLabel = isLegal ? "Persoană Juridică" : "Persoană Fizică";
  return (
    <div className="space-y-3">
      {bl && (
        <div data-testid="customer-blacklist-reason" className="rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
          <span className="font-semibold">Client în blacklist</span>{bl.created_at ? ` (din ${bl.created_at.slice(0, 10)})` : ""}: {bl.reason || "fără motiv"}
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span data-testid="customer-type-badge"
            className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${
              isLegal
                ? "border-violet-500/40 bg-violet-500/10 text-violet-200"
                : "border-sky-500/40 bg-sky-500/10 text-sky-200"
            }`}>
            <User className="h-3.5 w-3.5" /> Tip Client: {legalLabel}
          </span>
          {!!bl && (
            <span data-testid="customer-blacklisted" className="inline-flex items-center gap-1.5 rounded-md border border-rose-500/40 bg-rose-500/10 px-2 py-1 text-[11px] font-medium text-rose-300">
              <Ban className="h-3.5 w-3.5" /> Client în blacklist
            </span>
          )}
          {insight?.orders_count > 0 && (
            <span data-testid="customer-orders-count" className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[11px] font-medium text-amber-200">
              {insight.orders_count} {insight.orders_count === 1 ? "comandă" : "comenzi"} în total
            </span>
          )}
        </div>
        <button data-testid="add-blacklist-btn" onClick={onBlacklist} disabled={blacklisting || (!!bl && !isOwner)}
          className="inline-flex items-center gap-1.5 rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-1.5 text-xs font-medium text-rose-200 transition hover:bg-rose-500/20 disabled:opacity-50">
          <Ban className="h-3.5 w-3.5" /> {bl ? (isOwner ? "Scoate din blacklist" : "Deja în blacklist") : "Adaugă în Blacklist"}
        </button>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        <Field testId="customer-name" label="Nume" value={c.name} />
        <Field testId="customer-email" label="Email" value={c.email} />
        <Field testId="customer-phone" label="Telefon" value={c.phone_1 || c.phone_2 || c.phone_3} />
        <Field testId="customer-legal" label="Tip Client" value={legalLabel} />
        {isLegal && <Field testId="customer-company" label="Firmă" value={c.company} />}
        {isLegal && <Field testId="customer-code" label="Cod fiscal (CIF)" value={c.code} />}
        {isLegal && <Field testId="customer-regno" label="Nr. Înregistrare" value={c.registration_number} />}
        <Field testId="customer-iban" label="IBAN" value={c.iban} />
        <Field testId="customer-bank" label="Bancă" value={c.bank} />
      </div>
    </div>
  );
}

function AttachExternalForm({ kind, onSave, onCancel, busy }) {
  // Reusable inline form to attach an external invoice or AWB to an order.
  // `kind` is "invoice" or "awb" — controls label copy and palette only.
  const [url, setUrl] = useState("");
  const [number, setNumber] = useState("");
  const isAwb = kind === "awb";
  const accent = isAwb ? "sky" : "emerald";
  return (
    <div data-testid={`attach-${kind}-form`} className={`mt-2 rounded-lg border border-${accent}-500/30 bg-${accent}-500/5 p-3`}>
      <p className={`mb-2 text-[10px] font-semibold uppercase tracking-wider text-${accent}-200`}>
        Atașează {isAwb ? "AWB" : "factură"} existent{isAwb ? "" : "ă"} (link extern)
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        <input
          data-testid={`attach-${kind}-url`}
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder={isAwb ? "URL PDF AWB (https://...)" : "URL PDF factură (https://...)"}
          className={`rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-xs text-zinc-100 placeholder:text-zinc-600 focus:border-${accent}-500/60 focus:outline-none`}
        />
        <input
          data-testid={`attach-${kind}-number`}
          type="text"
          value={number}
          onChange={(e) => setNumber(e.target.value)}
          placeholder={isAwb ? "Număr AWB (ex: 1A2B3C...)" : "Număr factură (ex: BRAG 12345)"}
          className={`rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-xs text-zinc-100 placeholder:text-zinc-600 focus:border-${accent}-500/60 focus:outline-none`}
        />
      </div>
      <p className="mt-1.5 text-[10px] text-zinc-500">Cel puțin unul din câmpuri e necesar. Nu apelează Oblio/Sameday — doar atașează link-ul la comandă.</p>
      <div className="mt-2 flex gap-2">
        <button
          data-testid={`attach-${kind}-save`}
          onClick={() => onSave(url.trim(), number.trim())}
          disabled={busy || (!url.trim() && !number.trim())}
          className={`inline-flex items-center gap-1 rounded-md border border-${accent}-500/40 bg-${accent}-500/15 px-2.5 py-1 text-[11px] font-semibold text-${accent}-100 hover:bg-${accent}-500/25 disabled:opacity-50`}
        >
          <Save className="h-3 w-3" /> {busy ? "Se salvează..." : "Atașează"}
        </button>
        <button
          onClick={onCancel}
          disabled={busy}
          className="rounded-md border border-zinc-700 bg-zinc-900 px-2.5 py-1 text-[11px] text-zinc-300 hover:bg-zinc-800 disabled:opacity-50"
        >
          Anulează
        </button>
      </div>
    </div>
  );
}

function DocsTab({ order, onCreate, busy, onAction, actionBusy, onAttach, attachBusy }) {
  const otherDocs = (order.attachments || []).filter((a) => Number(a.type) !== 1 && Number(a.type) !== 3 && a.url);
  const hasInvoice = !!(order.invoice_url || order.invoice_number);
  const hasStorno = !!(order.storno_url || order.storno_number);
  const [attachOpen, setAttachOpen] = useState(false);
  const confirmAction = (msg) => window.confirm(msg);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <button data-testid="create-warranty-btn" onClick={() => onCreate("warranty")} disabled={busy === "warranty"}
          className="inline-flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-xs font-medium text-zinc-200 hover:border-violet-500/40 hover:text-violet-200 disabled:opacity-50">
          <Shield className="h-3.5 w-3.5" /> Creare Garanție
        </button>
        {!hasInvoice && (
          <button data-testid="create-invoice-btn" onClick={() => onCreate("invoice")} disabled={busy === "invoice"}
            className="inline-flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-xs font-medium text-zinc-200 hover:border-emerald-500/40 hover:text-emerald-200 disabled:opacity-50">
            <FilePlus className="h-3.5 w-3.5" /> Creare Factură
          </button>
        )}
        {!hasInvoice && !attachOpen && (
          <button data-testid="attach-invoice-btn" onClick={() => setAttachOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-xs font-medium text-zinc-200 hover:border-sky-500/40 hover:text-sky-200">
            <Link2 className="h-3.5 w-3.5" /> Atașează factură existentă
          </button>
        )}
        <button data-testid="create-service-btn" onClick={() => onCreate("service-sheet")} disabled={busy === "service-sheet"}
          className="inline-flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-xs font-medium text-zinc-200 hover:border-amber-500/40 hover:text-amber-200 disabled:opacity-50">
          <Wrench className="h-3.5 w-3.5" /> Creare Fișă Service
        </button>
      </div>

      {attachOpen && !hasInvoice && (
        <AttachExternalForm
          kind="invoice"
          busy={attachBusy === "invoice"}
          onCancel={() => setAttachOpen(false)}
          onSave={async (url, number) => {
            await onAttach("invoice", url, number);
            setAttachOpen(false);
          }}
        />
      )}

      <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-2.5">
        <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-zinc-500">Facturi</p>
        {!hasInvoice ? (
          <p className="text-xs text-zinc-600">Nicio factură atașată.</p>
        ) : (          <ul className="space-y-1.5">
            <li data-testid="invoice-row" className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-emerald-500/20 bg-zinc-950 px-2.5 py-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium text-emerald-200">{order.invoice_number || "Factură Oblio"}</p>
                {order.invoice_url && <p className="truncate text-[10px] text-zinc-500">{order.invoice_url}</p>}
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {order.invoice_url && (
                  <a href={order.invoice_url} target="_blank" rel="noreferrer" data-testid="invoice-open"
                    className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[10px] font-medium text-emerald-200 hover:bg-emerald-500/20">
                    <ExternalLink className="h-3 w-3" /> Deschide
                  </a>
                )}
                {!hasStorno && (
                  <button onClick={() => confirmAction(`Stornează factura ${order.invoice_number || ""}?\n\nAcțiunea creează în Oblio o factură STORNO (refund) care anulează contabil factura originală.`) && onAction("invoice-storno")}
                    disabled={actionBusy === "invoice-storno"} data-testid="invoice-storno-btn"
                    className="inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[10px] font-medium text-amber-200 hover:bg-amber-500/20 disabled:opacity-50">
                    <RotateCcw className="h-3 w-3" /> {actionBusy === "invoice-storno" ? "..." : "Stornează"}
                  </button>
                )}
                <button onClick={() => confirmAction("Șterge factura DOAR din platforma noastră?\n\nFactura RĂMÂNE în Oblio. Folosește această opțiune când vrei doar să detașezi link-ul din comandă.") && onAction("invoice-delete-local")}
                  disabled={actionBusy === "invoice-delete-local"} data-testid="invoice-delete-local-btn"
                  className="inline-flex items-center gap-1 rounded-md border border-rose-500/40 bg-rose-500/10 px-2 py-1 text-[10px] font-medium text-rose-200 hover:bg-rose-500/20 disabled:opacity-50">
                  <Trash2 className="h-3 w-3" /> {actionBusy === "invoice-delete-local" ? "..." : "Șterge local"}
                </button>
              </div>
            </li>
          </ul>
        )}
      </div>

      {hasStorno && (
        <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-2.5">
          <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-zinc-500">Storno</p>
          <ul className="space-y-1.5">
            <li data-testid="storno-row" className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-500/20 bg-zinc-950 px-2.5 py-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium text-amber-200">{order.storno_number || "Factură Storno"}</p>
                {order.storno_reference && <p className="text-[10px] text-zinc-500">Pentru: <span className="font-mono">{order.storno_reference}</span></p>}
              </div>
              <div className="flex items-center gap-1.5">
                {order.storno_url && (
                  <a href={order.storno_url} target="_blank" rel="noreferrer" data-testid="storno-open"
                    className="inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[10px] font-medium text-amber-200 hover:bg-amber-500/20">
                    <ExternalLink className="h-3 w-3" /> Deschide
                  </a>
                )}
                <button onClick={() => confirmAction("Șterge stornoul DOAR din platforma noastră?\n\nStorno-ul RĂMÂNE în Oblio.") && onAction("storno-delete-local")}
                  disabled={actionBusy === "storno-delete-local"} data-testid="storno-delete-local-btn"
                  className="inline-flex items-center gap-1 rounded-md border border-rose-500/40 bg-rose-500/10 px-2 py-1 text-[10px] font-medium text-rose-200 hover:bg-rose-500/20 disabled:opacity-50">
                  <Trash2 className="h-3 w-3" /> {actionBusy === "storno-delete-local" ? "..." : "Șterge local"}
                </button>
              </div>
            </li>
          </ul>
        </div>
      )}

      {order.warranty && (order.warranty.public_url || order.warranty.url) && (
        <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-2.5" data-testid="warranty-row">
          <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-zinc-500">Garanție</p>
          <ul className="space-y-1.5">
            <li className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-violet-500/20 bg-zinc-950 px-2.5 py-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium text-violet-200">{order.warranty.name || "Certificat de garanție"}</p>
                <p className="text-[10px] text-zinc-500">
                  {order.warranty.emag_pushed ? (
                    <span className="text-emerald-400">✓ Atașat în eMAG</span>
                  ) : (order.warranty.emag_push_error ? (
                    <span className="text-rose-400">eMAG push eșuat: {order.warranty.emag_push_error.slice(0, 80)}</span>
                  ) : (
                    <span>Salvat local (Gomag / site)</span>
                  ))}
                </p>
              </div>
              <div className="flex items-center gap-1.5">
                <a href={order.warranty.public_url || order.warranty.url} target="_blank" rel="noreferrer" data-testid="warranty-open"
                  className="inline-flex items-center gap-1 rounded-md border border-violet-500/40 bg-violet-500/10 px-2 py-1 text-[10px] font-medium text-violet-200 hover:bg-violet-500/20">
                  <ExternalLink className="h-3 w-3" /> Deschide
                </a>
              </div>
            </li>
          </ul>
        </div>
      )}

      <DocList data-testid="other-docs-list" title="Alte documente" emptyText="Niciun document suplimentar." docs={otherDocs} />
    </div>
  );
}

function DocList({ title, docs, emptyText }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-2.5">
      <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-zinc-500">{title}</p>
      {(!docs || docs.length === 0) ? (
        <p className="text-xs text-zinc-600">{emptyText}</p>
      ) : (
        <ul className="space-y-1.5">
          {docs.map((d, i) => (
            <li key={i} className="flex items-center justify-between gap-2 rounded-md border border-zinc-800 bg-zinc-950 px-2.5 py-1.5">
              <span className="truncate text-xs text-zinc-200">{d.name || d.url}</span>
              <a href={d.url} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[10px] font-medium text-emerald-200 hover:bg-emerald-500/20">
                <ExternalLink className="h-3 w-3" /> Deschide
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AwbStatusBadge({ orderId, integrationKey, hasAwb }) {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    if (!hasAwb) return;
    setLoading(true); setErr("");
    try {
      const q = integrationKey ? `?integration_key=${encodeURIComponent(integrationKey)}` : "";
      const r = await axios.get(`${API}/orders/${orderId}/awb/status${q}`);
      setS(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [orderId, hasAwb]);

  if (!hasAwb) return null;
  const stateStyles = {
    ready:     { cls: "border-amber-500/40 bg-amber-500/10 text-amber-200",   icon: "⏱" },
    on_route:  { cls: "border-sky-500/40 bg-sky-500/10 text-sky-200",         icon: "🚚" },
    delivered: { cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200", icon: "✓" },
    return:    { cls: "border-rose-500/50 bg-rose-500/15 text-rose-200 ring-1 ring-rose-500/20", icon: "↺" },
    return_delivered: { cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200", icon: "✓" },
    cancelled: { cls: "border-rose-500/40 bg-rose-500/10 text-rose-200",      icon: "✕" },
  };
  const cur = (s && stateStyles[s.state]) || null;
  return (
    <div data-testid="awb-status-row" className="mt-2 flex flex-wrap items-center gap-2">
      {loading && <span className="text-[10px] text-zinc-500">Se interoghează Sameday…</span>}
      {err && (
        <span className="inline-flex items-center gap-1 rounded-md border border-zinc-700 bg-zinc-900 px-2 py-0.5 text-[10px] text-zinc-400" title={err}>
          status indisponibil
        </span>
      )}
      {cur && s && (
        <>
          <span data-testid={`awb-state-${s.state}`}
            className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${cur.cls}`}>
            <span>{cur.icon}</span> {s.label}
          </span>
          {s.raw && s.raw !== s.label && <span className="text-[10px] text-zinc-500">· {s.raw}</span>}
          {s.status_date && <span className="text-[10px] text-zinc-600">· {new Date(s.status_date).toLocaleString("ro-RO")}</span>}
          <button onClick={load} disabled={loading} title="Reîmprospătează statusul"
            className="ml-1 rounded p-0.5 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200">
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
          </button>
        </>
      )}
    </div>
  );
}

function SecondaryAwbSection({ order, onChanged }) {
  const integKey = order._integration_key || "";
  const eligible =
    integKey.startsWith("emag_ro_fbe") ||
    integKey.startsWith("emag_ro_bau") ||
    integKey.startsWith("gomag") ||
    integKey.startsWith("shopify");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  if (!eligible) return null;

  const hasSecondary = !!(order.secondary_awb_url || order.secondary_awb_number);
  const secUrl = order.secondary_awb_url && order.secondary_awb_url.startsWith("/api/")
    ? `${BACKEND_URL}${order.secondary_awb_url}`
    : order.secondary_awb_url;

  const create = async () => {
    if (!window.confirm(
      "Se va genera un AWB Sameday adițional pentru această comandă.\n\n" +
      "• NU se va trimite la eMAG (marketplace-ul nu este notificat)\n" +
      "• Fără ramburs (COD = 0)\n" +
      "• Un singur colet standard\n\n" +
      "Continui?"
    )) return;
    setBusy(true); setErr("");
    try {
      await axios.post(
        `${API}/orders/${order.id}/secondary-awb`,
        integKey ? { integration_key: integKey } : {},
      );
      onChanged?.();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Șterge AWB-ul Secundar DOAR din platforma noastră?\n\nAWB-ul RĂMÂNE la Sameday.")) return;
    setBusy(true); setErr("");
    try {
      const q = integKey ? `?integration_key=${encodeURIComponent(integKey)}` : "";
      await axios.delete(`${API}/orders/${order.id}/secondary-awb/local${q}`);
      onChanged?.();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-purple-500/30 bg-purple-500/[0.04] p-2.5">
      <div className="mb-2 flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-medium uppercase tracking-wider text-purple-300">
            AWB Secundar
          </p>
          <p className="mt-0.5 text-[10px] text-zinc-400">
            Colet Sameday adițional pentru piese de schimb / retrimiteri.{" "}
            <span className="font-semibold text-amber-300">Nu se va trimite la Emag.</span>
          </p>
        </div>
        {!hasSecondary && (
          <button
            onClick={create}
            disabled={busy}
            data-testid="create-secondary-awb-btn"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-purple-500/40 bg-purple-500/10 px-3 py-1.5 text-xs font-medium text-purple-200 hover:bg-purple-500/20 disabled:opacity-50"
          >
            <Truck className="h-3.5 w-3.5" />
            {busy ? "Se generează…" : "Creează AWB Secundar"}
          </button>
        )}
      </div>

      {err && (
        <div className="mb-2 rounded border border-rose-500/40 bg-rose-500/10 px-2 py-1 text-[11px] text-rose-200">
          {err}
        </div>
      )}

      {hasSecondary && (
        <div data-testid="secondary-awb-row" className="rounded-md border border-purple-500/20 bg-zinc-950 px-2.5 py-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-medium text-purple-200">
                {order.secondary_awb_number || "AWB Secundar"}
              </p>
              <p className="text-[10px] text-zinc-500">
                {order.secondary_awb_shipping_type && (
                  <>tip: <span className="font-mono">{order.secondary_awb_shipping_type}</span> · </>
                )}
                {order.secondary_awb_created_at && (
                  <>creat: <span className="font-mono">{new Date(order.secondary_awb_created_at).toLocaleString("ro-RO")}</span></>
                )}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {secUrl && (
                <a
                  href={secUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="secondary-awb-open"
                  className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[10px] font-medium text-emerald-200 hover:bg-emerald-500/20"
                >
                  <ExternalLink className="h-3 w-3" /> Deschide PDF
                </a>
              )}
              <button
                onClick={remove}
                disabled={busy}
                data-testid="secondary-awb-delete-local-btn"
                className="inline-flex items-center gap-1 rounded-md border border-rose-500/40 bg-rose-500/10 px-2 py-1 text-[10px] font-medium text-rose-200 hover:bg-rose-500/20 disabled:opacity-50"
              >
                <Trash2 className="h-3 w-3" /> {busy ? "..." : "Șterge local"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AwbTab({ order, onOpenGenerator, onAction, actionBusy, onAttach, attachBusy, onChanged }) {
  // NOTE: We intentionally do NOT pull `findAttachment(order, 3)` here. eMAG
  // attachment type 3 is the WARRANTY PDF — listing those under "AWB-uri eMAG"
  // was a wrong label that opened the warranty when the user clicked "Deschide".
  // The warranty has its own dedicated tab.
  const hasCurrent = !!(order.awb_url || order.awb_number);
  const [attachOpen, setAttachOpen] = useState(false);
  const confirmAction = (msg) => window.confirm(msg);
  const awbUrl = order.awb_url && order.awb_url.startsWith("/api/")
    ? `${BACKEND_URL}${order.awb_url}` : order.awb_url;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {!hasCurrent ? (
          <>
            <button data-testid="generate-awb-btn" onClick={onOpenGenerator}
              className="inline-flex items-center gap-1.5 rounded-md border border-sky-500/40 bg-sky-500/10 px-3 py-1.5 text-xs font-medium text-sky-200 hover:bg-sky-500/20">
              <Truck className="h-3.5 w-3.5" /> Generează AWB
            </button>
            {!attachOpen && (
              <button data-testid="attach-awb-btn" onClick={() => setAttachOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-xs font-medium text-zinc-200 hover:border-sky-500/40 hover:text-sky-200">
                <Link2 className="h-3.5 w-3.5" /> Atașează AWB existent
              </button>
            )}
          </>
        ) : (
          <p className="text-[11px] text-zinc-500">Un AWB este deja activ. Anulează-l sau șterge-l ca să poți emite altul.</p>
        )}
      </div>

      {attachOpen && !hasCurrent && (
        <AttachExternalForm
          kind="awb"
          busy={attachBusy === "awb"}
          onCancel={() => setAttachOpen(false)}
          onSave={async (url, number) => {
            await onAttach("awb", url, number);
            setAttachOpen(false);
          }}
        />
      )}

      <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-2.5">
        <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-zinc-500">AWB curent</p>
        {!hasCurrent ? (
          <p className="text-xs text-zinc-600">Niciun AWB emis încă pentru această comandă.</p>
        ) : (
          <ul className="space-y-1.5">
            <li data-testid="awb-row" className="rounded-md border border-sky-500/20 bg-zinc-950 px-2.5 py-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium text-sky-200">{order.awb_number || "AWB"}</p>
                  <p className="text-[10px] text-zinc-500">
                    status eMAG: <span className="font-mono">{order.awb_emag_push_status || "—"}</span>
                    {order.awb_created_at && <> · creat: <span className="font-mono">{new Date(order.awb_created_at).toLocaleString("ro-RO")}</span></>}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {awbUrl && (
                    <a href={awbUrl} target="_blank" rel="noreferrer" data-testid="awb-open"
                      className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[10px] font-medium text-emerald-200 hover:bg-emerald-500/20">
                      <ExternalLink className="h-3 w-3" /> Deschide PDF
                    </a>
                  )}
                  <button onClick={() => confirmAction(`Anulează AWB-ul ${order.awb_number} în Sameday?\n\nAcțiunea contactează Sameday și marchează AWB-ul ca anulat. Aceasta NU se poate undo.`) && onAction("awb-cancel")}
                    disabled={actionBusy === "awb-cancel"} data-testid="awb-cancel-btn"
                    className="inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[10px] font-medium text-amber-200 hover:bg-amber-500/20 disabled:opacity-50">
                    <Ban className="h-3 w-3" /> {actionBusy === "awb-cancel" ? "..." : "Anulează la Sameday"}
                  </button>
                  <button onClick={() => confirmAction("Șterge AWB-ul DOAR din platforma noastră?\n\nAWB-ul RĂMÂNE la Sameday. Folosește această opțiune când AWB-ul a fost deja livrat sau dorești să detașezi link-ul din comandă.") && onAction("awb-delete-local")}
                    disabled={actionBusy === "awb-delete-local"} data-testid="awb-delete-local-btn"
                    className="inline-flex items-center gap-1 rounded-md border border-rose-500/40 bg-rose-500/10 px-2 py-1 text-[10px] font-medium text-rose-200 hover:bg-rose-500/20 disabled:opacity-50">
                    <Trash2 className="h-3 w-3" /> {actionBusy === "awb-delete-local" ? "..." : "Șterge local"}
                  </button>
                </div>
              </div>
              <AwbStatusBadge orderId={order.id} integrationKey={order._integration_key} hasAwb={hasCurrent} />
              {(() => {
                // Feb 2026 (user request) — SURSA DE ADEVĂR pentru costul AWB
                // e xlsx-ul „desfășurător" primit de la Sameday. Am eliminat
                // complet calculul cu formulă și butoanele „Reîmprospătează"
                // / „Editează manual". Dacă xlsx-ul încă n-a venit → PENDING.
                const integ = order._integration_key || "";
                const eligible = integ === "gomag_bragus" || integ === "manual_bragus" || integ === "shopify_bragus";
                if (!eligible) return null;
                if (!order.awb_number) return null;
                return <SamedayInvoicedCostBlock awbNumber={order.awb_number} />;
              })()}
            </li>
          </ul>
        )}
      </div>

      <SecondaryAwbSection order={order} onChanged={onChanged} />

      <EmagAwbLookupPanel order={order} />
    </div>
  );
}

/**
 * Cost AWB Sameday (facturat) — sursa unică de adevăr = xlsx desfășurător
 * primit de la Sameday 2x/lună. Dacă AWB-ul nu e încă în xlsx → PENDING
 * (badge portocaliu). User request Feb 2026: „elimin calculul cu formule".
 */
function SamedayInvoicedCostBlock({ awbNumber }) {
  const [loading, setLoading] = useState(true);
  const [inv, setInv] = useState(null);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/sameday/awb/${encodeURIComponent(awbNumber)}/invoiced`)
      .then((r) => { if (alive) setInv(r.data); })
      .catch(() => { if (alive) setInv({ pending: true }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [awbNumber]);
  if (loading) {
    return <div className="mt-2 text-[11px] italic text-zinc-500">Se verifică cost facturat...</div>;
  }
  if (!inv || inv.pending) {
    return (
      <div className="mt-2 flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1.5" data-testid="awb-cost-pending">
        <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-200">Pending</span>
        <span className="text-[11px] text-amber-100">Cost AWB va fi disponibil când Sameday trimite desfășurătorul (2×/lună)</span>
      </div>
    );
  }
  const total = Number(inv.total_facturat || 0);
  return (
    <div className="mt-2 grid grid-cols-2 gap-2" data-testid="awb-cost-invoiced">
      <div className="rounded-md border border-sky-500/30 bg-sky-500/5 px-2 py-1.5" title="Total facturat de Sameday (fără TVA)">
        <div className="text-[9px] uppercase text-sky-300/80">Cost AWB (facturat)</div>
        <div className="font-mono text-sm font-bold text-sky-300">{total.toFixed(2)} RON</div>
      </div>
      <div className="rounded-md border border-zinc-800 bg-zinc-900/50 px-2 py-1.5" title="Serviciu Sameday">
        <div className="text-[9px] uppercase text-zinc-500">Serviciu</div>
        <div className="text-xs text-zinc-300">{inv.service || "—"}</div>
      </div>
      {inv.line_type && inv.line_type !== "delivery" && (
        <div className="col-span-2 rounded-md border border-violet-500/30 bg-violet-500/5 px-2 py-1 text-[10px] uppercase text-violet-200">
          Tip: {inv.line_type.replace("_", " ")}
          {inv.observatii ? ` — ${inv.observatii}` : ""}
        </div>
      )}
    </div>
  );
}

/**
 * eMAG AWB Lookup — calls `/api/orders/{id}/fetch-emag-awb` which probes
 * eMAG's `/awb/read` endpoint. Useful for FBE orders where eMAG owns the
 * AWB and never sent us the number; also works on BAU as a cross-check.
 *
 * Intentionally rendered ONLY inside the AWB tab (per operator request) —
 * never exposed as a button on the Orders list rows.
 */
function EmagAwbLookupPanel({ order }) {
  const isEmag = (order._integration_key || "").startsWith("emag_");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  if (!isEmag) return null;

  const cachedAwb = order.fbe_awb_number;
  const cachedAt = order.fbe_awb_fetched_at;

  const onFetch = async () => {
    setBusy(true); setError(""); setResult(null);
    try {
      const r = await axios.post(`${API}/orders/${order._emag_id}/fetch-emag-awb`);
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-amber-500/20 bg-amber-500/[0.04] p-2.5" data-testid="emag-awb-lookup">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-[10px] font-medium uppercase tracking-wider text-amber-300">
          AWB eMAG <span className="text-zinc-500 normal-case">(citire directă din contul eMAG)</span>
        </p>
        <button
          data-testid="emag-awb-fetch-btn"
          onClick={onFetch}
          disabled={busy}
          className="inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[10px] font-medium text-amber-200 hover:bg-amber-500/20 disabled:opacity-50"
        >
          {busy ? "..." : (cachedAwb ? "Reîmprospătează" : "Extrage AWB de la eMAG")}
        </button>
      </div>

      {cachedAwb && (
        <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 px-2.5 py-1.5">
          <p className="text-[10px] text-zinc-500">Număr AWB salvat de eMAG</p>
          <p className="font-mono text-sm font-semibold text-emerald-200" data-testid="emag-awb-number">{cachedAwb}</p>
          {order.fbe_awb_courier && (
            <p className="mt-0.5 text-[10px] text-zinc-500">Curier: <span className="text-zinc-300">{order.fbe_awb_courier}</span></p>
          )}
          {cachedAt && (
            <p className="mt-0.5 text-[9px] text-zinc-600">Citit la {new Date(cachedAt).toLocaleString("ro-RO")}</p>
          )}
        </div>
      )}

      {result && result.found && result.awb_number !== cachedAwb && (
        <div className="mt-2 rounded-md border border-sky-500/30 bg-sky-500/5 px-2.5 py-1.5">
          <p className="font-mono text-sm font-semibold text-sky-200">{result.awb_number}</p>
          {result.count > 1 && (
            <p className="text-[9px] text-zinc-500">eMAG a returnat {result.count} AWB-uri pentru această comandă (primul fără anulare e cel afișat).</p>
          )}
        </div>
      )}

      {result && !result.found && (
        <p className="text-[10px] text-zinc-500" data-testid="emag-awb-not-found">{result.message}</p>
      )}

      {error && (
        <p className="mt-1 text-[10px] text-rose-300" data-testid="emag-awb-error">{error}</p>
      )}

      {!cachedAwb && !result && !error && (
        <p className="text-[10px] text-zinc-600">Citire directă din eMAG, READ-ONLY · nu modifică nimic upstream.</p>
      )}
    </div>
  );
}

function AddressBlock({ name, phone, street, suburb, city, postal, country, county }) {
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
      <Field label="Nume" value={name} />
      <Field label="Telefon" value={phone} />
      <Field label="Adresă" value={street} />
      <Field label="Sub-localitate" value={suburb} />
      <Field label="Cod poștal" value={postal} />
      <Field label="Țară" value={country} />
      <Field label="Județ/Regiune" value={county} />
      <Field label="Localitate" value={city} />
    </div>
  );
}

function EditableAddressBlock({ order, kind, onSaved }) {
  // Inline editor for billing/shipping address on Gomag orders.
  // `kind` is "billing" or "shipping" — selects the field prefix.
  // Renders a read-only AddressBlock by default + an "Editează" pill. On
  // click, swaps to a 2-column form with text inputs + county/city dropdowns
  // populated from Sameday's catalog (so the user can't typo their way into
  // a broken AWB).
  const c = order.customer || {};
  const isBilling = kind === "billing";
  const prefix = isBilling ? "billing" : "shipping";
  const initial = {
    name: isBilling ? (c.billing_name || c.name || "") : (c.shipping_contact || c.name || ""),
    phone: isBilling ? (c.billing_phone || c.phone_1 || "") : (c.shipping_phone || c.phone_1 || ""),
    street: c[`${prefix}_street`] || "",
    suburb: c[`${prefix}_suburb`] || c[`${prefix}_county`] || "",
    city: c[`${prefix}_city`] || "",
    postal: c[`${prefix}_postal_code`] || "",
    country: c[`${prefix}_country`] || "RO",
    county: c[`${prefix}_county`] || c[`${prefix}_suburb`] || "",
    // CUI/CIF — doar pentru billing (user cerinta Oct 2026)
    cui: isBilling ? (c.code || "") : "",
    regcom: isBilling ? (c.registration_number || "") : "",
  };
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(initial);
  const [counties, setCounties] = useState([]);
  const [cities, setCities] = useState([]);
  const [anafLoading, setAnafLoading] = useState(false);
  const [anafError, setAnafError] = useState(null);

  useEffect(() => { setForm(initial); /* eslint-disable-next-line */ }, [order?.id, editing]);

  // Load counties on first edit. Sorted by Sameday already.
  useEffect(() => {
    if (!editing || counties.length) return;
    axios.get(`${API}/sameday/counties?country=${(form.country || "RO").toUpperCase()}`)
      .then((r) => setCounties(r.data?.items || []))
      .catch(() => {/* silent — fallback to text */});
    // eslint-disable-next-line
  }, [editing]);

  // Load cities whenever county changes (we look up the county id by name).
  useEffect(() => {
    if (!editing || !counties.length || !form.county) { setCities([]); return; }
    // Diacritic-insensitive match: Sameday returns "București" (with diacritic)
    // but Shopify / Gomag orders often store "Bucuresti" (no diacritic). Strict
    // `===` lower-case comparison was failing silently and the city dropdown
    // never got populated (user report 2026-10-02 — order 1016 Bucuresti).
    const norm = (s) => (s || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
    const target = norm(form.county);
    const match = counties.find((x) => norm(x.name) === target);
    if (!match) { setCities([]); return; }
    axios.get(`${API}/sameday/cities?county_id=${match.id}&country=${(form.country || "RO").toUpperCase()}`)
      .then((r) => setCities(r.data?.items || []))
      .catch(() => setCities([]));
    // eslint-disable-next-line
  }, [editing, counties.length, form.county, form.country]);

  const field = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    const _cui = String(form.cui || "").trim();
    const payload = isBilling
      ? {
          billing_name: form.name, billing_phone: form.phone,
          billing_street: form.street, billing_city: form.city,
          billing_postal_code: form.postal, billing_country: form.country,
          billing_suburb: form.county, billing_county: form.county,
          // CUI/CIF: daca user-ul l-a completat, marcam comanda ca "pe firma"
          // automat (legal_entity=1). Daca sterge CUI-ul la editare, nu
          // facem downgrade — ramane cum era.
          ...(_cui ? { code: _cui, legal_entity: 1 } : {}),
          ...(form.regcom ? { registration_number: String(form.regcom).trim() } : {}),
        }
      : {
          shipping_contact: form.name, shipping_phone: form.phone,
          shipping_street: form.street, shipping_city: form.city,
          shipping_postal_code: form.postal, shipping_country: form.country,
          shipping_suburb: form.county, shipping_county: form.county,
        };
    try {
      await axios.patch(`${API}/orders/${order.id}/customer`, payload);
      setEditing(false);
      onSaved?.();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  // CUI → ANAF; fara CUI → nume firma → openapi.ro → ANAF. Backend-ul
  // salveaza direct datele pe comanda (se pastreaza si la re-sync Shopify).
  const [nameCandidates, setNameCandidates] = useState(order.company_resolution?.candidates || []);
  useEffect(() => { setNameCandidates(order.company_resolution?.candidates || []); }, [order?.company_resolution]);

  const resolveCompany = async (body) => {
    setAnafLoading(true); setAnafError(null);
    try {
      const r = await axios.post(`${API}/orders/${order.id}/resolve-company`, {
        integration_key: order._integration_key, ...body,
      });
      const d = r.data || {};
      if (d.status === "resolved") {
        setNameCandidates([]); setEditing(false); onSaved?.();
        return;
      }
      setNameCandidates(d.candidates || []);
      setAnafError(d.message || "Firma nu a putut fi identificata");
    } catch (e) {
      setAnafError(e?.response?.data?.detail || e.message || "Cautare esuata");
    } finally { setAnafLoading(false); }
  };

  const lookupAnaf = () => {
    const cuiRaw = String(form.cui || "").trim().toUpperCase().replace(/^RO/i, "");
    const nameRaw = String(form.name || "").trim();
    if (cuiRaw && /^\d{2,10}$/.test(cuiRaw)) return resolveCompany({ cui: cuiRaw });
    if (!cuiRaw && nameRaw.length >= 3) return resolveCompany({ name: nameRaw });
    setAnafError("Introdu CUI sau un nume de firma de min. 3 caractere");
  };

  const pickCandidate = (cui) => resolveCompany({ cui });

  const candidatesList = nameCandidates.length > 0 && (
    <CompanyCandidates items={nameCandidates} onPick={pickCandidate} disabled={anafLoading} />
  );

  if (!editing) {
    const _needsCui = isBilling && Number(c.legal_entity) === 1 && !(c.code || "").trim();
    const _res = order.company_resolution || {};
    return (
      <div className="space-y-2">
        {isBilling && (c.code || Number(c.legal_entity) === 1) && (
          <div className={`rounded-lg border p-2 text-[11px] ${_needsCui ? "border-rose-500/50 bg-rose-500/10 text-rose-200" : "border-emerald-500/30 bg-emerald-500/5 text-emerald-200"}`} data-testid="billing-cui-indicator">
            {_needsCui ? (
              <>
                <strong>Lipseste CUI</strong> — {_res.message || "firma nu a putut fi identificata automat"}. Poti cauta din nou sau completa CUI-ul manual din „Editeaza datele".
                <div className="mt-2">
                  <button data-testid="auto-resolve-company-btn" type="button" disabled={anafLoading}
                    onClick={() => resolveCompany({})}
                    className="inline-flex items-center gap-1 rounded-md border border-sky-500/40 bg-sky-500/15 px-2 py-1 text-[11px] font-semibold text-sky-100 hover:bg-sky-500/25 disabled:opacity-50">
                    {anafLoading ? <RefreshCw className="h-3 w-3 animate-spin" /> : <ExternalLink className="h-3 w-3" />}
                    {anafLoading ? "Caut..." : "Cauta firma automat"}
                  </button>
                </div>
                {anafError && <p className="mt-1 text-[10px] text-rose-300" data-testid="auto-resolve-error">{anafError}</p>}
                {candidatesList}
              </>
            ) : (
              <>
                <strong>CUI:</strong> <span className="font-mono" data-testid="billing-cui-value">{c.code}</span>
                {c.registration_number && <> · <strong>Reg. Com.:</strong> <span className="font-mono">{c.registration_number}</span></>}
                {_res.status === "resolved" && (
                  <span className="ml-2 rounded bg-emerald-500/20 px-1 text-[9px] text-emerald-300" data-testid="company-resolved-badge">
                    {_res.source === "openapi+anaf" ? "IDENTIFICAT AUTOMAT DUPA NUME + ANAF" : "VERIFICAT ANAF"}
                  </span>
                )}
              </>
            )}
          </div>
        )}
        <AddressBlock name={initial.name} phone={initial.phone} street={initial.street}
          suburb={initial.suburb} city={initial.city} postal={initial.postal}
          country={initial.country} county={initial.county} />
        <button data-testid={`edit-${kind}-btn`} onClick={() => setEditing(true)}
          className="inline-flex items-center gap-1 rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1 text-[11px] text-zinc-300 hover:border-orange-500/40 hover:text-orange-200">
          ✎ Editează datele
        </button>
      </div>
    );
  }

  return (
    <div data-testid={`edit-${kind}-form`} className="space-y-2 rounded-lg border border-orange-500/30 bg-orange-500/5 p-3">
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-orange-200">
        Editează {isBilling ? "datele de facturare" : "datele de livrare"}
      </p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {isBilling && (
          <>
            <div className="sm:col-span-2 lg:col-span-3 rounded-md border border-sky-500/30 bg-sky-500/5 p-2">
              <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-sky-200">
                CUI / CIF {Number(c.legal_entity) === 1 && <span className="ml-1 text-rose-400">* obligatoriu pentru firme</span>}
              </label>
              <div className="flex gap-2 flex-wrap items-stretch">
                <input data-testid="edit-billing-cui" type="text" value={form.cui}
                  onChange={field("cui")} placeholder="ex: 41911688 sau RO41911688"
                  className="flex-1 min-w-[150px] rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-xs text-zinc-100 placeholder:text-zinc-600 focus:border-sky-500/60 focus:outline-none" />
                <button data-testid="verify-anaf-btn" type="button" onClick={lookupAnaf} disabled={anafLoading}
                  className="inline-flex items-center gap-1 rounded-md border border-sky-500/40 bg-sky-500/15 px-3 py-1.5 text-[11px] font-semibold text-sky-100 hover:bg-sky-500/25 disabled:opacity-50">
                  {anafLoading ? <RefreshCw className="h-3 w-3 animate-spin" /> : <ExternalLink className="h-3 w-3" />}
                  {anafLoading ? "Verific..." : "Verifica ANAF"}
                </button>
              </div>
              {anafError && (
                <p className="mt-1 text-[10px] text-rose-300" data-testid="anaf-error">{anafError}</p>
              )}
              {candidatesList}
              <p className="mt-1 text-[10px] text-sky-400/60">
                Daca CUI-ul lipseste, lasa gol si da click „Verifica ANAF" — cautam firma dupa nume prin openapi.ro.
              </p>
            </div>
            <FieldInput label="Nr. Reg. Comertului" testId="edit-billing-regcom" value={form.regcom} onChange={field("regcom")} />
          </>
        )}
        <FieldInput label={isBilling ? "Denumire firma" : "Nume"} testId={`edit-${kind}-name`} value={form.name} onChange={field("name")} />
        <FieldInput label="Telefon" testId={`edit-${kind}-phone`} value={form.phone} onChange={field("phone")} />
        <FieldInput label="Adresă" testId={`edit-${kind}-street`} value={form.street} onChange={field("street")} />
        <FieldInput label="Cod poștal" testId={`edit-${kind}-postal`} value={form.postal} onChange={field("postal")} />
        <FieldInput label="Țară" testId={`edit-${kind}-country`} value={form.country} onChange={field("country")} />
        <FieldSelect label="Județ" testId={`edit-${kind}-county`} value={form.county}
          options={counties.map((x) => x.name)}
          onChange={(v) => setForm((f) => ({ ...f, county: v, city: "" }))} />
        <FieldSelect label="Localitate" testId={`edit-${kind}-city`} value={form.city}
          options={cities.map((x) => x.name)}
          onChange={(v) => setForm((f) => ({ ...f, city: v }))}
          disabled={!form.county || cities.length === 0} />
      </div>
      <div className="flex gap-2 pt-1">
        <button data-testid={`save-${kind}-btn`} onClick={save} disabled={saving}
          className="inline-flex items-center gap-1 rounded-md border border-orange-500/50 bg-orange-500/15 px-3 py-1 text-[11px] font-semibold text-orange-100 hover:bg-orange-500/25 disabled:opacity-50">
          <Save className="h-3 w-3" /> {saving ? "Se salvează..." : "Salvează"}
        </button>
        <button onClick={() => setEditing(false)} disabled={saving}
          className="rounded-md border border-zinc-700 bg-zinc-900 px-3 py-1 text-[11px] text-zinc-300 hover:bg-zinc-800 disabled:opacity-50">
          Anulează
        </button>
      </div>
    </div>
  );
}

function CompanyCandidates({ items, onPick, disabled }) {
  return (
    <div className="mt-2 space-y-1" data-testid="anaf-name-candidates">
      <p className="text-[10px] text-sky-300/80">Alege firma corecta ({items.length} rezultate):</p>
      {items.map((x) => (
        <button key={x.cui} type="button" onClick={() => onPick(x.cui)} disabled={disabled}
          data-testid={`anaf-pick-${x.cui}`}
          className={`flex w-full items-center justify-between rounded-md border px-2 py-1 text-left text-[11px] transition-colors disabled:opacity-50 ${x.exact_match ? "border-emerald-500/50 bg-emerald-500/10 hover:bg-emerald-500/15" : "border-zinc-700 bg-zinc-900 hover:bg-zinc-800"}`}>
          <span className="truncate">
            <strong className="text-zinc-100">{x.name}</strong>
            <span className="ml-2 text-zinc-500">· {x.county}</span>
            {x.exact_match && <span className="ml-2 rounded bg-emerald-500/20 px-1 text-emerald-300 text-[9px]">EXACT</span>}
          </span>
          <span className="font-mono text-zinc-400 shrink-0 ml-2">{x.cui}</span>
        </button>
      ))}
    </div>
  );
}

function FieldInput({ label, testId, value, onChange }) {
  return (
    <div>
      <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-zinc-500">{label}</label>
      <input data-testid={testId} type="text" value={value || ""} onChange={onChange}
        className="w-full rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-xs text-zinc-100 placeholder:text-zinc-600 focus:border-orange-500/60 focus:outline-none" />
    </div>
  );
}

function FieldSelect({ label, testId, value, options, onChange, disabled }) {
  // Combobox-ish: native select with current value pre-selected (or a free-form
  // option for backwards-compat with addresses where Sameday catalog didn't load).
  const knownValue = options.includes(value);
  return (
    <div>
      <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-zinc-500">{label}</label>
      <select data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={disabled}
        className="w-full rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-xs text-zinc-100 focus:border-orange-500/60 focus:outline-none disabled:opacity-50">
        <option value="">— alege —</option>
        {!knownValue && value && <option value={value}>{value} (curent)</option>}
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function BillingTab({ order, onChanged }) {
  const c = order.customer || {};
  const isGomag = (order._integration_key || "").toLowerCase().startsWith("gomag") || (order._integration_key || "").toLowerCase().startsWith("shopify");
  const county = c.billing_county || c.shipping_county || c.billing_suburb || c.shipping_suburb || "";
  if (isGomag) return <EditableAddressBlock order={order} kind="billing" onSaved={onChanged} />;
  return <AddressBlock name={c.billing_name || c.name} phone={c.billing_phone || c.phone_1} street={c.billing_street}
    suburb={c.billing_suburb} city={c.billing_city} postal={c.billing_postal_code} country={c.billing_country} county={county} />;
}

function ShippingTab({ order, onChanged }) {
  const c = order.customer || {};
  const isGomag = (order._integration_key || "").toLowerCase().startsWith("gomag") || (order._integration_key || "").toLowerCase().startsWith("shopify");
  const county = c.shipping_county || c.shipping_suburb || c.billing_county || c.billing_suburb || "";
  const mode = order.delivery_mode || "";
  const modeLower = mode.toLowerCase();
  // eMAG locker info lives in details.courier_external_office_id; Gomag stores top-level locker_id + locker_name.
  const details = order.details || {};
  const isLocker = !!order.locker_id || /locker|easybox|pachetomat|pickup|ridicare|magazin/.test(modeLower) || !!details.courier_external_office_id;
  const lockerId = order.locker_id || details.courier_external_office_id || "";
  const lockerName = order.locker_name || details.locker_name || "";
  const rawDel = (order._raw_gomag && order._raw_gomag.delivery) || {};
  const lockerAddress = rawDel.lockerAddress || order.locker_address || "";
  const lockerCity = rawDel.lockerCity || order.locker_city || "";
  const lockerPostal = rawDel.lockerZipcode || order.locker_zipcode || "";
  return (
    <div className="space-y-4">
      <div>
        <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-zinc-500">Locație de livrare</p>
        {isLocker ? (
          <div data-testid="delivery-locker" className="rounded-lg border border-sky-500/30 bg-sky-500/5 p-3">
            <div className="mb-2 flex items-center gap-2 flex-wrap">
              <MapPinned className="h-4 w-4 text-sky-300" />
              <span className="text-xs font-semibold uppercase tracking-wider text-sky-200">Pachetomat / Easybox</span>
              {(order._integration_key || "").toLowerCase().startsWith("shopify") ? (
                lockerId && lockerId !== "?" ? (
                  <span data-testid="locker-status-selected"
                    className="ml-auto inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/15 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-200">
                    ● Selectat
                  </span>
                ) : (
                  <span data-testid="locker-status-not-selected"
                    className="ml-auto inline-flex items-center gap-1 rounded-full border border-rose-500/40 bg-rose-500/15 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-rose-200 animate-pulse">
                    ● Neselectat
                  </span>
                )
              ) : null}
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              <Field testId="locker-id" label="ID Locker" value={lockerId ? <span className="font-mono">{lockerId}</span> : null} />
              <Field testId="locker-name" label="Nume locker" value={lockerName} />
              <Field testId="locker-mode" label="Modalitate" value={mode} />
              {lockerAddress && <Field testId="locker-address" label="Adresă locker" value={lockerAddress} />}
              {lockerCity && <Field testId="locker-city" label="Oraș" value={lockerCity} />}
              {lockerPostal && <Field testId="locker-postal" label="Cod poștal" value={lockerPostal} />}
            </div>
            {(order._integration_key || "").toLowerCase().startsWith("shopify") && !order.awb_number && order._shopify_id && (
              <ShopifyLockerPicker order={order} onSaved={onChanged} />
            )}
          </div>
        ) : (
          <div data-testid="delivery-home" className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3">
            <div className="mb-2 flex items-center gap-2">
              <MapPin className="h-4 w-4 text-emerald-300" />
              <span className="text-xs font-semibold uppercase tracking-wider text-emerald-200">Livrare la domiciliu</span>
              {mode && <span className="text-[10px] text-zinc-500">· {mode}</span>}
            </div>
            <AddressBlock name={c.shipping_contact || c.name} phone={c.shipping_phone || c.phone_1} street={c.shipping_street}
              suburb={c.shipping_suburb} city={c.shipping_city} postal={c.shipping_postal_code} country={c.shipping_country} county={county} />
            {isGomag && (
              <div className="mt-3 border-t border-emerald-500/15 pt-3">
                <EditableAddressBlock order={order} kind="shipping" onSaved={onChanged} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// =========================================================================
// Profit tab — fetches /api/orders/{id}/profit and renders the full
// breakdown row-by-row in Romanian so the user can audit each component.
// =========================================================================
function ProfitRow({ label, amount, sub, tone = "default", testid, currency }) {
  const sign = amount < 0 ? "-" : amount > 0 ? "+" : "";
  const toneClass = {
    revenue: "text-emerald-300",
    cost: "text-rose-300",
    neutral: "text-zinc-300",
    default: "text-zinc-200",
  }[tone];
  return (
    <div data-testid={testid} className="flex items-center justify-between border-b border-zinc-800/60 px-3 py-2.5 last:border-b-0">
      <div className="min-w-0">
        <p className="text-xs font-medium text-zinc-200">{label}</p>
        {sub && <p className="mt-0.5 text-[10px] text-zinc-500">{sub}</p>}
      </div>
      <div className={`text-sm font-semibold tabular-nums ${toneClass}`}>
        {sign}{fmtMoney(Math.abs(amount), currency)}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// HistoryTab — timeline unificat per comanda (user cerinta Oct 2026)
// Afiseaza evenimentele scrise in `order_events` din backend. Comenzile
// vechi (importate inainte de deploy) returneaza listă goala cu mesaj
// explicativ.
// ──────────────────────────────────────────────────────────────────

const EVENT_ICON = {
  order_imported: Package,
  order_status_changed: ArrowRightLeft,
  email_sent: Mail,
  whatsapp_sent: Mail,
  invoice_created: FileCheck2,
  storno_created: Receipt,
  awb_generated: Truck,
  shopify_neridicata_flow: PackageX,
  shopify_cod_paid: CheckCircle2,
  shopify_fulfillment_delivered: CheckCircle2,
  note_added: FileText,
  // AWB state events are prefixed `awb_<state>`.
};

const EVENT_COLOR = {
  order_imported: "bg-sky-500/10 text-sky-300 border-sky-500/30",
  order_status_changed: "bg-indigo-500/10 text-indigo-300 border-indigo-500/30",
  email_sent: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  whatsapp_sent: "bg-green-500/10 text-green-300 border-green-500/30",
  invoice_created: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  storno_created: "bg-rose-500/10 text-rose-300 border-rose-500/30",
  awb_generated: "bg-cyan-500/10 text-cyan-300 border-cyan-500/30",
  shopify_neridicata_flow: "bg-orange-500/10 text-orange-300 border-orange-500/30",
  shopify_cod_paid: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  shopify_fulfillment_delivered: "bg-teal-500/10 text-teal-300 border-teal-500/30",
  note_added: "bg-zinc-500/10 text-zinc-300 border-zinc-500/30",
};

function pickEventIcon(evType) {
  if (EVENT_ICON[evType]) return EVENT_ICON[evType];
  if ((evType || "").startsWith("awb_")) return Truck;
  return Clock;
}
function pickEventColor(evType) {
  if (EVENT_COLOR[evType]) return EVENT_COLOR[evType];
  if ((evType || "").startsWith("awb_")) return "bg-cyan-500/10 text-cyan-300 border-cyan-500/30";
  return "bg-zinc-500/10 text-zinc-300 border-zinc-500/30";
}

function HistoryTab({ order }) {
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    axios.get(`${API}/orders/${encodeURIComponent(order.id)}/events?limit=500`)
      .then((r) => { if (!cancelled) setEvents(r.data?.items || []); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.detail || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [order?.id]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-zinc-400" data-testid="history-loading">
        <RefreshCw className="h-4 w-4 animate-spin" /> Se încarcă istoricul...
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-300" data-testid="history-error">
        Eroare la încărcarea istoricului: {error}
      </div>
    );
  }
  if (!events || events.length === 0) {
    return (
      <div className="rounded-lg border border-zinc-700/50 bg-zinc-900/50 p-6 text-center" data-testid="history-empty">
        <History className="mx-auto h-10 w-10 text-zinc-600" />
        <p className="mt-3 text-sm font-semibold text-zinc-300">Nicio înregistrare în istoric</p>
        <p className="mt-1 text-xs text-zinc-500">
          Această comandă fie este anterioară activării istoricului (Oct 2026),
          fie nu s-a întâmplat încă nimic cu ea. Toate acțiunile viitoare
          (email-uri trimise, facturi, AWB-uri, schimbări de status, etc.)
          vor apărea aici automat.
        </p>
      </div>
    );
  }

  const fmt = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("ro-RO", { dateStyle: "medium", timeStyle: "medium" });
    } catch { return iso; }
  };

  return (
    <div className="space-y-2" data-testid="history-tab">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs text-zinc-400">
          {events.length} {events.length === 1 ? "eveniment" : "evenimente"} · ordonate cronologic
        </p>
      </div>
      <ol className="relative space-y-3 border-l-2 border-zinc-800 pl-6">
        {events.map((ev, idx) => {
          const Icon = pickEventIcon(ev.event_type);
          const colorCls = pickEventColor(ev.event_type);
          return (
            <li key={idx} className="relative" data-testid={`history-event-${idx}`}>
              <span className={`absolute -left-[33px] flex h-6 w-6 items-center justify-center rounded-full border ${colorCls}`}>
                <Icon className="h-3 w-3" />
              </span>
              <div className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-3">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium text-zinc-100">{ev.title}</p>
                  <span className="shrink-0 text-[10px] text-zinc-500">{fmt(ev.created_at)}</span>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] text-zinc-500">
                  <span className={`rounded px-1.5 py-0.5 font-mono ${colorCls}`}>{ev.event_type}</span>
                  {ev.actor && ev.actor !== "system" && <span>· {ev.actor}</span>}
                  {ev.platform && <span>· {ev.platform}</span>}
                </div>
                {ev.meta && Object.keys(ev.meta).length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-[10px] text-zinc-500 hover:text-zinc-300">
                      Detalii tehnice
                    </summary>
                    <pre className="mt-1 overflow-x-auto rounded bg-black/40 p-2 text-[10px] text-zinc-400">
                      {JSON.stringify(ev.meta, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function ProfitTab({ order }) {
  const [profit, setProfit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    const k = order._integration_key ? `?integration_key=${encodeURIComponent(order._integration_key)}` : "";
    axios.get(`${API}/orders/${order.id}/profit${k}`)
      .then((r) => { if (!cancelled) setProfit(r.data); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.detail || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [order?.id, order?._integration_key]);

  if (loading) {
    return <div data-testid="profit-loading" className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-8 text-center text-sm text-zinc-500">Se calculează profitul...</div>;
  }
  if (error) {
    return <div data-testid="profit-error" className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-4 text-sm text-rose-200">Eroare: {error}</div>;
  }
  if (!profit) return null;

  // The profit endpoint converts everything to RON via BNR FX for BG/HU
  // orders (and returns `currency: "RON"`). Use that — NOT `order.currency`
  // — otherwise BG orders display RON values labelled as EUR, making the
  // profit number look weirdly larger than the order total.
  const c = profit.currency || "RON";
  const positive = profit.profit >= 0;
  const orderCurrency = (order.currency || "RON").toUpperCase();
  const isFxConverted = orderCurrency !== c;

  return (
    <div className="space-y-4" data-testid="profit-tab">
      {/* Summary card */}
      <div className={`rounded-xl border ${positive ? "border-emerald-500/30 bg-emerald-500/5" : "border-rose-500/30 bg-rose-500/5"} p-4`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] font-medium uppercase tracking-wider text-zinc-400">Profit final</p>
            <p data-testid="profit-final" className={`mt-1 text-3xl font-bold tabular-nums ${positive ? "text-emerald-300" : "text-rose-300"}`}>
              {fmtMoney(profit.profit, c)}
            </p>
            {isFxConverted && (
              <p className="mt-0.5 text-[10px] text-amber-300/80">
                Convertit din {orderCurrency} → RON (BNR live). Total comandă: {fmtMoney(order.computed_total, orderCurrency)}.
              </p>
            )}
            <p className="mt-0.5 text-xs text-zinc-400">Marjă: <span className={positive ? "text-emerald-300 font-semibold" : "text-rose-300 font-semibold"}>{profit.margin_pct}%</span> <span className="text-[10px] text-zinc-500">(profit ÷ achiziție+vamă)</span></p>
          </div>
          <div className="flex flex-col items-end gap-1">
            {profit.is_fbe && <span className="rounded-md border border-sky-500/40 bg-sky-500/10 px-2 py-0.5 text-[10px] font-medium text-sky-200">FBE</span>}
            {profit.is_genius && <span className="rounded-md border border-violet-500/40 bg-violet-500/10 px-2 py-0.5 text-[10px] font-medium text-violet-200">Genius</span>}
          </div>
        </div>
      </div>

      {/* Formula explanation */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3 text-[11px] leading-relaxed text-zinc-400">
        <p className="font-semibold text-zinc-300 mb-1">Formula calcul:</p>
        <p>
          <span className="text-emerald-300">Preț vânzare fără TVA</span> (înainte de voucher)
          {" − "}<span className="text-rose-300">Preț achiziție</span>
          {" − "}<span className="text-rose-300">Reciclare / alte costuri</span>
          {profit.is_fbe ? <> {" − "}<span className="text-rose-300">Comision FBE</span></> : null}
          {" − "}<span className="text-rose-300">Comision eMAG</span> (% din preț vânzare)
          {" − "}<span className="text-rose-300">Taxă vamală</span> (% din preț achiziție)
          {" − "}<span className="text-rose-300">Cost ads</span> (% din preț vânzare)
          {profit.is_genius ? <> {" − "}<span className="text-rose-300">Taxă Genius</span></> : null}
        </p>
      </div>

      {/* Detailed breakdown */}
      <div className="overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900/40">
        <p className="border-b border-zinc-800 px-3 py-2 text-[10px] font-medium uppercase tracking-wider text-zinc-500">Detaliu calcul</p>
        <ProfitRow currency={c} testid="profit-revenue" label="Venit (preț vânzare fără TVA, înainte de voucher)" amount={profit.revenue} tone="revenue" />
        <ProfitRow currency={c} testid="profit-buy" label="Cost achiziție produse" amount={-profit.cost_buy} tone="cost"
             sub="Suma prețurilor de achiziție per produs × cantitate" />
        {profit.cost_other > 0 && (
          <ProfitRow currency={c} testid="profit-other" label="Reciclare / alte costuri" amount={-profit.cost_other} tone="cost" />
        )}
        {profit.is_fbe && (
          <ProfitRow currency={c} testid="profit-comm-fbe" label="Comision FBE" amount={-(profit.cost_commission_fbe || 0)} tone="cost"
               sub="Sumă fixă RON per bucată (Fulfillment by eMAG)" />
        )}
        <ProfitRow currency={c} testid="profit-comm-emag" label="Comision eMAG" amount={-(profit.cost_commission_emag ?? profit.cost_commission)} tone="cost"
             sub="% aplicat pe prețul de vânzare fără TVA" />
        {profit.cost_customs > 0 && (
          <ProfitRow currency={c} testid="profit-customs" label="Taxă vamală" amount={-profit.cost_customs} tone="cost"
               sub="% aplicat pe prețul de achiziție fără TVA" />
        )}
        {profit.cost_ads > 0 && (
          <ProfitRow currency={c} testid="profit-ads" label="Cost ads / promovare" amount={-profit.cost_ads} tone="cost"
               sub="% din prețul de vânzare fără TVA" />
        )}
        {profit.is_genius && profit.genius_fee > 0 && (
          <ProfitRow currency={c} testid="profit-genius" label="Taxă Genius" amount={-profit.genius_fee} tone="cost"
               sub="Comisia eMAG Genius (variabilă pe țară / categorie)" />
        )}
        {/* TOTAL */}
        <div className={`flex items-center justify-between px-3 py-2.5 ${positive ? "bg-emerald-500/10" : "bg-rose-500/10"}`}>
          <p className="text-xs font-bold uppercase tracking-wider text-zinc-100">Profit</p>
          <p data-testid="profit-total" className={`text-base font-bold tabular-nums ${positive ? "text-emerald-300" : "text-rose-300"}`}>
            {fmtMoney(profit.profit, c)}
          </p>
        </div>
      </div>

      {/* Voucher informational note */}
      {profit.voucher_total !== 0 && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-[11px] text-amber-200">
          <span className="font-semibold">Voucher (informativ):</span> {fmtMoney(profit.voucher_total, c)}.
          Voucherele NU se scad din venit pentru calculul profitului — profitul se calculează pe prețul de vânzare integral (fără TVA), conform cerinței.
        </div>
      )}

      {/* Per-product breakdown */}
      {profit.lines && profit.lines.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-zinc-800">
          <p className="border-b border-zinc-800 bg-zinc-900/60 px-3 py-2 text-[10px] font-medium uppercase tracking-wider text-zinc-500">Detaliu pe produs</p>
          <table className="w-full text-xs" data-testid="profit-lines-table">
            <thead className="bg-zinc-900/40 text-left text-[9px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-2 py-2">Produs</th>
                <th className="px-2 py-2 text-right">Cant.</th>
                <th className="px-2 py-2 text-right">Venit</th>
                <th className="px-2 py-2 text-right">Achiziție</th>
                <th className="px-2 py-2 text-right">Comision</th>
                {profit.lines.some((l) => l.taxa_vamala > 0) && <th className="px-2 py-2 text-right">Vamă</th>}
                {profit.lines.some((l) => l.cost_ads > 0) && <th className="px-2 py-2 text-right">Ads</th>}
                {profit.lines.some((l) => l.alte_costuri > 0) && <th className="px-2 py-2 text-right">Alte</th>}
                <th className="px-2 py-2 text-right">Profit linie</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800 text-zinc-200">
              {profit.lines.map((l, i) => (
                <tr key={i} data-testid={`profit-line-${i}`} className="bg-zinc-950/30">
                  <td className="px-2 py-2 truncate max-w-xs" title={l.name}>{l.name || "—"}</td>
                  <td className="px-2 py-2 text-right text-zinc-400">{l.qty}</td>
                  <td className="px-2 py-2 text-right text-emerald-300">{fmtMoney(l.line_revenue, c)}</td>
                  <td className="px-2 py-2 text-right text-rose-300">-{fmtMoney(l.pret_achizitie, c)}</td>
                  <td className="px-2 py-2 text-right text-rose-300">-{fmtMoney(l.comision, c)}</td>
                  {profit.lines.some((x) => x.taxa_vamala > 0) && <td className="px-2 py-2 text-right text-rose-300">{l.taxa_vamala ? `-${fmtMoney(l.taxa_vamala, c)}` : "—"}</td>}
                  {profit.lines.some((x) => x.cost_ads > 0) && <td className="px-2 py-2 text-right text-rose-300">{l.cost_ads ? `-${fmtMoney(l.cost_ads, c)}` : "—"}</td>}
                  {profit.lines.some((x) => x.alte_costuri > 0) && <td className="px-2 py-2 text-right text-rose-300">{l.alte_costuri ? `-${fmtMoney(l.alte_costuri, c)}` : "—"}</td>}
                  <td className={`px-2 py-2 text-right font-semibold ${l.line_profit >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{fmtMoney(l.line_profit, c)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function ProductsSummary({ order }) {
  const products = order.products || [];
  const [profit, setProfit] = useState(null);
  useEffect(() => {
    // Only fetch profit for finalised/completed orders (status 4) — the spec
    // calls for it to show "pe fiecare comandă finalizată". The endpoint
    // works regardless of status but we don't want to spam it for orders
    // still in flight.
    if (!order?.id || Number(order.status) !== 4) { setProfit(null); return; }
    const k = order._integration_key ? `?integration_key=${encodeURIComponent(order._integration_key)}` : "";
    axios.get(`${API}/orders/${order.id}/profit${k}`)
      .then((r) => setProfit(r.data))
      .catch(() => setProfit(null));
  }, [order?.id, order?.status, order?._integration_key]);
  return (
    <div>
      <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-zinc-500">Sumar produse</p>
      <div className="overflow-hidden rounded-lg border border-zinc-800">
        <table className="w-full text-xs" data-testid="products-summary-table">
          <thead className="bg-zinc-900/80 text-left text-[9px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="px-2 py-2">Produs</th>
              <th className="px-2 py-2">SKU</th>
              <th className="px-2 py-2 text-right">Cant.</th>
              <th className="px-2 py-2 text-right">Preț fără TVA</th>
              <th className="px-2 py-2 text-right">TVA</th>
              <th className="px-2 py-2 text-right">Preț cu TVA</th>
              <th className="px-2 py-2 text-right">Subtotal</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800/80">
            {products.map((p, i) => {
              const qty = Number(p.quantity || 0);
              const priceNoVat = Number(p.sale_price || 0);
              const vatRate = Number(p.vat || 0);
              const priceWithVat = priceNoVat * (1 + vatRate);
              const subtotal = priceWithVat * qty;
              return (
                <tr key={i} data-testid={`summary-row-${i}`} className="hover:bg-zinc-900/40">
                  <td className="px-2 py-2 align-top">
                    <div className="flex items-start gap-2">
                      <ProductThumb src={p.image} alt={p.name} />
                      <div className="min-w-0 flex-1">
                        <p className="line-clamp-2 text-zinc-100">{p.name}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-2 align-top font-mono text-[10px] text-zinc-400">{p.ext_part_number || p.part_number || `#${p.product_id}`}</td>
                  <td className="px-2 py-2 align-top text-right">{qty}</td>
                  <td className="px-2 py-2 align-top text-right">{fmtMoney(priceNoVat, order.currency)}</td>
                  <td className="px-2 py-2 align-top text-right text-zinc-300">{(vatRate * 100).toFixed(0)}%</td>
                  <td className="px-2 py-2 align-top text-right">{fmtMoney(priceWithVat, order.currency)}</td>
                  <td className="px-2 py-2 align-top text-right font-semibold text-zinc-100">{fmtMoney(subtotal, order.currency)}</td>
                </tr>
              );
            })}
            {order.shipping_tax > 0 && (() => {
              // Transport line — split into Net + TVA + Total like product rows.
              // For Gomag we store shipping_vat / shipping_vat_rate already; for
              // eMAG (which only stores `shipping_tax` net) we fall back to the
              // dominant product VAT rate, or 21% if products have no VAT data.
              const shipNet = Number(order.shipping_tax || 0);
              let shipVat = Number(order.shipping_vat || 0);
              let shipRate = Number(order.shipping_vat_rate || 0);
              if (!shipVat) {
                if (!shipRate) {
                  const firstProdVat = (order.products || []).map((p) => Number(p.vat || 0)).find((v) => v > 0);
                  shipRate = firstProdVat || 0.21;
                }
                shipVat = shipNet * shipRate;
              } else if (!shipRate && shipNet > 0) {
                shipRate = shipVat / shipNet;
              }
              const shipGross = shipNet + shipVat;
              return (
                <tr className="bg-zinc-900/40" data-testid="shipping-row">
                  <td colSpan={2} className="px-2 py-2 text-zinc-400">Transport</td>
                  <td className="px-2 py-2 text-right text-zinc-500">1</td>
                  <td className="px-2 py-2 text-right">{fmtMoney(shipNet, order.currency)}</td>
                  <td className="px-2 py-2 text-right text-zinc-300">{(shipRate * 100).toFixed(0)}%</td>
                  <td className="px-2 py-2 text-right">{fmtMoney(shipGross, order.currency)}</td>
                  <td className="px-2 py-2 text-right font-semibold text-zinc-100">{fmtMoney(shipGross, order.currency)}</td>
                </tr>
              );
            })()}
            {(order.vouchers || []).map((v, i) => {
              // Support both voucher formats:
              //   • eMAG  → { sale_price (NEG net), sale_price_vat (NEG vat) }
              //   • Gomag → { value (POS gross), vat ("21"), name, voucher }
              const isGomagFmt = (v.value !== undefined && v.value !== null && v.sale_price === undefined);
              let net, vatAmt, total, vatRate;
              if (isGomagFmt) {
                const gross = Math.abs(Number(v.value || 0));
                const vatPctNum = Number(v.vat || 0); // e.g. 21 (percent)
                vatRate = vatPctNum > 0 ? (vatPctNum / 100) : 0;
                // gross = net * (1 + vatRate) → net = gross / (1 + vatRate)
                net = vatRate > 0 ? -(gross / (1 + vatRate)) : -gross;
                vatAmt = -(gross + net); // negative VAT component (note: net is negative)
                total = -gross;
              } else {
                net = Number(v.sale_price || 0);
                vatAmt = Number(v.sale_price_vat || 0);
                total = net + vatAmt;
                vatRate = Math.abs(vatAmt) > 0.001 && Math.abs(net) > 0 ? (Math.abs(vatAmt) / Math.abs(net)) : 0;
              }
              // eMAG marks a voucher as "with VAT" via non-zero sale_price_vat.
              // Both `sale_price` and `sale_price_vat` are NEGATIVE (deduction),
              // so we test the absolute value, not `> 0`.
              const hasVat = Math.abs(vatAmt) > 0.001;
              const name = v.voucher_name || v.name || v.id || "Voucher";
              const isCodReducere = /cod reducere/i.test(name);
              const labelPrefix = isCodReducere ? "Discount conform" : "Reducere conform voucher:";
              const displayName = isCodReducere ? name : (name.startsWith("Reducere") || name.startsWith("Discount") ? name : `${labelPrefix} ${name}`);
              const offered = (v.offered_by || "").toLowerCase();
              const offeredLabel = offered === "emag" ? "Oferit de eMAG" : (offered === "seller" ? "Oferit de seller" : null);
              return (
                <tr key={`v-${i}`} className="bg-rose-500/[0.04]">
                  <td colSpan={2} className="px-2 py-2 text-rose-300" title={hasVat ? "Voucher cu TVA" : "Voucher fără TVA"}>
                    <span className="font-medium">{displayName}</span>
                    <span className="ml-2 inline-flex rounded border border-rose-500/30 px-1 py-0.5 text-[9px] uppercase tracking-wider text-rose-300/80">
                      {hasVat ? "cu TVA" : "fără TVA"}
                    </span>
                    {offeredLabel && (
                      <span className={`ml-1.5 inline-flex rounded border px-1 py-0.5 text-[9px] uppercase tracking-wider ${offered === "emag" ? "border-sky-500/30 text-sky-300/90" : "border-amber-500/30 text-amber-300/90"}`}>
                        {offeredLabel}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-right text-zinc-500">1</td>
                  <td className="px-2 py-2 text-right text-rose-300">{fmtMoney(-Math.abs(net), order.currency)}</td>
                  <td className="px-2 py-2 text-right text-zinc-300">{(vatRate * 100).toFixed(0)}%</td>
                  <td className="px-2 py-2 text-right text-rose-300">{fmtMoney(-Math.abs(total), order.currency)}</td>
                  <td className="px-2 py-2 text-right font-semibold text-rose-300">{fmtMoney(-Math.abs(total), order.currency)}</td>
                </tr>
              );
            })}
            <tr className="bg-zinc-900">
              <td colSpan={6} className="px-2 py-2.5 text-right text-sm font-semibold text-zinc-300">TOTAL</td>
              <td data-testid="summary-total" className="px-2 py-2.5 text-right text-base font-bold text-orange-200">{fmtMoney(order.computed_total, order.currency)}</td>
            </tr>
            {/* Profit margin — only on finalized orders. Pulls breakdown
                from /api/orders/{id}/profit which subtracts purchase price,
                eMAG/FBE commission, ads, other costs and the Genius fee. */}
            {profit && (
              <tr data-testid="summary-profit-row" className="bg-emerald-500/5">
                <td colSpan={6} className="px-2 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-emerald-200">
                  Marja profit
                  <span className="ml-2 inline-flex items-center gap-1.5 text-[10px] font-normal normal-case tracking-normal text-emerald-300/70">
                    {profit.is_genius && <span title={`Comisia Genius (${profit.genius_fee} ${order.currency}) inclusă`}>· Genius</span>}
                    <span title={`Cost vânzare (fără TVA): ${fmtMoney(profit.revenue, order.currency)} | Achiziție: ${fmtMoney(profit.cost_buy, order.currency)} | Comision eMAG: ${fmtMoney(profit.cost_commission_emag ?? profit.cost_commission, order.currency)}${profit.is_fbe ? ` | Comision FBE: ${fmtMoney(profit.cost_commission_fbe || 0, order.currency)}` : ""} | Alte costuri: ${fmtMoney(profit.cost_other, order.currency)} | Ads: ${fmtMoney(profit.cost_ads, order.currency)}${profit.cost_customs ? ` | Taxă vamală: ${fmtMoney(profit.cost_customs, order.currency)}` : ""}${profit.is_genius ? ` | Genius: ${fmtMoney(profit.genius_fee, order.currency)}` : ""}${profit.voucher_total ? ` | Voucher (informativ): ${fmtMoney(profit.voucher_total, order.currency)}` : ""}`} className="cursor-help underline decoration-dotted">
                      ({profit.margin_pct}%)
                    </span>
                  </span>
                </td>
                <td data-testid="summary-profit" className={`px-2 py-2 text-right text-sm font-bold ${profit.profit >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                  {fmtMoney(
                    profit.profit_native != null ? profit.profit_native : profit.profit,
                    profit.currency_native || order.currency || "RON",
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function OrderDetailModal({ order, open, onClose, onChanged }) {
  const [activeTab, setActiveTab] = useState("info");
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [blacklisting, setBlacklisting] = useState(false);
  const { user } = useAuth() || {};
  const isOwner = user?.role === "owner";
  const [blOpen, setBlOpen] = useState(false);
  const [insight, setInsight] = useState(null);
  const loadInsight = () => {
    const c = order?.customer || {};
    if (!open || !order) return;
    axios.get(`${API}/customers/insight`, { params: { phone: c.phone_1 || c.phone_2 || undefined, email: c.email || undefined } })
      .then((r) => setInsight(r.data)).catch(() => setInsight(null));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(loadInsight, [open, order?.id]);
  const [awbGenOpen, setAwbGenOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReasons, setCancelReasons] = useState([]);
  const [cancelCode, setCancelCode] = useState(1);
  const [cancelText, setCancelText] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [invoicePreview, setInvoicePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [emitting, setEmitting] = useState(false);
  const [actionBusy, setActionBusy] = useState(null);
  const [attachBusy, setAttachBusy] = useState(null);

  // eslint-disable-next-line
  useEffect(() => { if (open) { setActiveTab("info"); setError(""); setSuccess(""); } }, [open, order?.id]);
  // eslint-disable-next-line
  useEffect(() => {
    if (open && cancelReasons.length === 0) {
      axios.get(`${API}/orders/cancel-reasons`).then((r) => setCancelReasons(r.data?.reasons || [])).catch(() => {});
    }
  }, [open]);
  // Auto-trigger the invoice preview when the modal was opened by the
  // "Creează factură" button on the Orders table row.
  // eslint-disable-next-line
  useEffect(() => {
    if (open && order?._autoAction === "invoice" && !invoicePreview && !previewLoading) {
      handleCreate("invoice");
    }
  }, [open, order?.id, order?._autoAction]);

  if (!open || !order) return null;

  const handleCreate = async (kind) => {
    // For invoices we go through a preview step first so the user can review
    // exactly what will be sent to Oblio.
    if (kind === "invoice") {
      setPreviewLoading(true); setError(""); setSuccess("");
      try {
        const integ_key = order._integration_key || order.integration_key;
        const r = await axios.post(`${API}/orders/${order.id}/invoice`, {
          dry_run: true, integration_key: integ_key,
        });
        setInvoicePreview(r.data);
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      } finally { setPreviewLoading(false); }
      return;
    }
    setBusy(kind); setError(""); setSuccess("");
    try {
      const r = await axios.post(`${API}/orders/${order.id}/${kind}`);
      setSuccess(r.data?.message || `Operațiune reușită.`);
      onChanged?.();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setBusy(null); }
  };

  const handleEmitInvoice = async () => {
    setEmitting(true); setError(""); setSuccess("");
    try {
      const integ_key = order._integration_key || order.integration_key;
      const r = await axios.post(`${API}/orders/${order.id}/invoice`, {
        integration_key: integ_key,
      });
      const invNum = r.data?.number || r.data?.invoice_number || "OK";
      setSuccess(`Factură ${invNum} emisă`);
      setInvoicePreview(null);
      onChanged?.();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setEmitting(false); }
  };

  // Manual attach of an external invoice or AWB. `kind` is "invoice" | "awb".
  // Posts to /api/orders/{id}/{kind}/attach with { url, number } — no calls
  // to Oblio/Sameday/marketplace. After the patch the orders list shows the
  // standard green "Deschide" button just like an organically issued doc.
  const handleAttach = async (kind, url, number) => {
    setAttachBusy(kind); setError(""); setSuccess("");
    try {
      const integ_key = order._integration_key;
      const q = integ_key ? `?integration_key=${encodeURIComponent(integ_key)}` : "";
      await axios.post(`${API}/orders/${order.id}/${kind}/attach${q}`, { url, number });
      setSuccess(`${kind === "invoice" ? "Factură" : "AWB"} atașat cu succes.`);
      onChanged?.();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setAttachBusy(null); }
  };

  // Unified handler for the per-row actions in DocsTab / AwbTab.
  // Always relies on `onChanged()` to refresh order data — avoids in-place
  // mutation of the `order` prop (which trips the immutability lint rule).
  const handleAction = async (kind) => {
    setActionBusy(kind); setError(""); setSuccess("");
    const integ_key = order._integration_key;
    const q = integ_key ? `?integration_key=${encodeURIComponent(integ_key)}` : "";
    try {
      if (kind === "invoice-storno") {
        const r = await axios.post(`${API}/orders/${order.id}/invoice/storno`, {});
        const url = r.data?.url; const num = r.data?.storno_number;
        if (url) { try { window.open(url, "_blank", "noopener,noreferrer"); } catch (_e) { /* popup blocked */ } }
        setSuccess(`Storno emis: ${num || "OK"}`);
      } else if (kind === "invoice-delete-local") {
        await axios.delete(`${API}/orders/${order.id}/invoice/local${q}`);
        setSuccess("Factura a fost detașată local. Factura rămâne în Oblio.");
      } else if (kind === "storno-delete-local") {
        await axios.delete(`${API}/orders/${order.id}/storno/local${q}`);
        setSuccess("Stornoul a fost detașat local. Rămâne în Oblio.");
      } else if (kind === "awb-cancel") {
        const r = await axios.post(`${API}/orders/${order.id}/awb/cancel`, integ_key ? { integration_key: integ_key } : {});
        setSuccess(`AWB ${r.data?.awb_number || ""} anulat la Sameday.`);
      } else if (kind === "awb-delete-local") {
        await axios.delete(`${API}/orders/${order.id}/awb/local${q}`);
        setSuccess("AWB-ul a fost detașat local. Rămâne la Sameday.");
      }
      onChanged?.();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setActionBusy(null); }
  };

  const handleBlacklist = async () => {
    const bl = insight?.blacklist;
    if (!bl) return setBlOpen(true);
    if (!isOwner || !window.confirm(`Scoți clientul din blacklist?\nMotiv actual: ${bl.reason || "—"}`)) return;
    setBlacklisting(true);
    try {
      await axios.post(`${API}/customers/blacklist/${bl.id}/remove`);
      setSuccess("Client scos din blacklist");
      loadInsight(); onChanged?.();
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    setBlacklisting(false);
  };
  const blWarn = (what) => !insight?.blacklist || window.confirm(`Atenție: clientul e în blacklist.\nMotiv: ${insight.blacklist.reason || "—"}\n\nContinui cu ${what}?`);

  const isGomag = (order._integration_key || "").startsWith("gomag") || (order._integration_key || "").startsWith("shopify");
  const alreadyCancelled = Number(order.status) === 0;

  const handleForceSync = async () => {
    setResyncing(true); setError(""); setSuccess("");
    try {
      // Re-pull this single order from its source (eMAG/Gomag) and refresh DB.
      const r = await axios.post(`${API}/orders/${order.id}/refresh`, { integration_key: order._integration_key });
      setSuccess(`Comanda #${order.id} re-sincronizată cu succes.`);
      onChanged?.();
      // best-effort close + reopen the modal via parent re-fetch
      if (r.data?.order) {
        // keep open — parent's onChanged should refresh the row data
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setResyncing(false); }
  };

  const handleCancelSubmit = async () => {
    setCancelling(true); setError(""); setSuccess("");
    try {
      const body = { integration_key: order._integration_key };
      if (isGomag) {
        body.reason_text = cancelText.trim();
        if (!body.reason_text) { setError("Motivul anulării este obligatoriu pentru Gomag."); setCancelling(false); return; }
      } else {
        body.reason_code = Number(cancelCode);
      }
      const r = await axios.post(`${API}/orders/${order.id}/cancel`, body);
      setSuccess(`Comanda a fost anulată. ${r.data?.reason_label || r.data?.reason || ""}`);
      setCancelOpen(false);
      onChanged?.();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setCancelling(false); }
  };

  return (
    <div data-testid="order-detail-modal" className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center sm:p-4">
      <div className="flex h-[95vh] w-full max-w-5xl flex-col overflow-hidden rounded-t-2xl border border-zinc-800 bg-zinc-950 shadow-2xl shadow-black/60 sm:rounded-2xl">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b border-zinc-800 bg-zinc-900/60 px-4 py-3">
          <div className="flex min-w-0 items-center gap-3">
            {(() => {
              // For Gomag orders display the upstream eMAG/marketplace order
              // number (`_gomag_number`, e.g. 3114498072) as PRIMARY and keep
              // the small internal Gomag id (e.g. 6504) as secondary.
              const isGomag = (order._integration_key || "").startsWith("gomag") || (order._integration_key || "").startsWith("shopify");
              const primary = isGomag && order._gomag_number ? order._gomag_number : order.id;
              const secondary = isGomag && order._gomag_number ? order.id : null;
              return (
                <span className="inline-flex items-center gap-1.5">
                  <span className="rounded-md bg-orange-500/10 px-2 py-1 font-mono text-xs font-semibold text-orange-200">#{primary}</span>
                  {secondary && (
                    <span title="ID intern Gomag" className="font-mono text-[10px] text-zinc-500">int. #{secondary}</span>
                  )}
                </span>
              );
            })()}
            <span className="truncate text-sm text-zinc-300">{order.customer?.name || "—"}</span>
            <span className="hidden text-zinc-700 sm:inline">·</span>
            <span className="hidden font-mono text-[10px] text-zinc-500 sm:inline">{PLATFORM_LABEL[order._integration_key] || order._integration_key}</span>
            {order.cancellation_request && !alreadyCancelled && (
              <span data-testid="cancel-request-pill" className="ml-1 inline-flex items-center gap-1 rounded-full border border-rose-500/50 bg-rose-500/15 px-2 py-0.5 text-[10px] font-medium text-rose-200">
                <Ban className="h-3 w-3" /> Cerere anulare
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="header-blacklist-btn" onClick={handleBlacklist} disabled={blacklisting || (!!insight?.blacklist && !isOwner)}
              title={insight?.blacklist ? `Motiv: ${insight.blacklist.reason || "—"}` : "Adaugă clientul în blacklist"}
              className="inline-flex items-center gap-1.5 rounded-md border border-rose-500/40 bg-rose-500/10 px-2.5 py-1.5 text-[11px] font-medium text-rose-200 transition hover:bg-rose-500/20 disabled:opacity-60">
              <Ban className="h-3.5 w-3.5" /> {insight?.blacklist ? (isOwner ? "Scoate din blacklist" : "BLACKLISTED") : "Adaugă în blacklist"}
            </button>
            <button data-testid="force-resync-btn" onClick={handleForceSync} disabled={resyncing}
              title="Re-sincronizează această comandă din platforma sursă"
              className="inline-flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-900 px-2.5 py-1.5 text-[11px] font-medium text-zinc-200 transition hover:border-orange-500/40 hover:bg-orange-500/10 hover:text-orange-200 disabled:opacity-50">
              <RefreshCw className={`h-3.5 w-3.5 ${resyncing ? "animate-spin" : ""}`} /> {resyncing ? "Sync…" : "Sync forțat"}
            </button>
            {!alreadyCancelled && (
              <button data-testid="cancel-order-btn" onClick={() => setCancelOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-md border border-rose-500/40 bg-rose-500/10 px-2.5 py-1.5 text-[11px] font-medium text-rose-200 transition hover:bg-rose-500/20">
                <Ban className="h-3.5 w-3.5" /> Anulează
              </button>
            )}
            <button data-testid="modal-close" onClick={onClose} className="rounded-md p-1.5 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 overflow-x-auto border-b border-zinc-800 bg-zinc-950 px-2 py-2">
          {TABS.map((t) => <TabBtn key={t.id} tab={t} active={activeTab === t.id} onClick={() => setActiveTab(t.id)} />)}
        </div>

        {/* Banners */}
        {(error || success) && (
          <div className="px-4 pt-3">
            {error && <div data-testid="modal-error" className="flex items-start gap-2 rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200"><AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" /><span className="flex-1">{error}</span><button onClick={() => setError("")} className="opacity-70 hover:opacity-100"><X className="h-3.5 w-3.5" /></button></div>}
            {success && <div data-testid="modal-success" className="mt-2 flex items-start gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200"><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" /><span className="flex-1">{success}</span><button onClick={() => setSuccess("")} className="opacity-70 hover:opacity-100"><X className="h-3.5 w-3.5" /></button></div>}
          </div>
        )}

        {/* Content */}
        {/* Content (scrollable) */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === "info" && <InfoTab order={order} onChanged={onChanged} />}
          {activeTab === "customer" && <CustomerTab order={order} onBlacklist={handleBlacklist} blacklisting={blacklisting} insight={insight} isOwner={isOwner} />}
          {activeTab === "profit" && <ProfitTab order={order} />}
          {activeTab === "docs" && <DocsTab order={order} onCreate={(k) => (k !== "invoice" || blWarn("factura")) && handleCreate(k)} busy={busy} onAction={handleAction} actionBusy={actionBusy} onAttach={handleAttach} attachBusy={attachBusy} />}
          {activeTab === "awb" && <AwbTab order={order} onOpenGenerator={() => blWarn("AWB-ul") && setAwbGenOpen(true)} onAction={handleAction} actionBusy={actionBusy} onAttach={handleAttach} attachBusy={attachBusy} onChanged={onChanged} />}
          {activeTab === "billing" && <BillingTab order={order} onChanged={onChanged} />}
          {activeTab === "shipping" && <ShippingTab order={order} onChanged={onChanged} />}
          {activeTab === "history" && <HistoryTab order={order} />}
        </div>

        {/* Sticky product summary — visible on every tab */}
        <div className="border-t border-zinc-800 bg-zinc-950 px-4 py-3" data-testid="sticky-products-summary">
          <ProductsSummary order={order} />
        </div>
      </div>

      <BlacklistDialog order={order} open={blOpen} onClose={() => setBlOpen(false)} onSaved={() => { loadInsight(); onChanged?.(); }} />
      <AwbGeneratorModal key={order.id} order={order} open={awbGenOpen} onClose={() => setAwbGenOpen(false)}
        onCreated={() => { onChanged?.(); }} />

      {/* Invoice preview dialog */}
      {invoicePreview && (
        <div data-testid="invoice-preview-dialog" className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4">
          <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-xl border border-emerald-500/30 bg-zinc-950 shadow-2xl">
            <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-3">
              <div className="flex items-center gap-2 text-emerald-200">
                <FileText className="h-4 w-4" />
                <span className="text-sm font-semibold">Preview factură pentru comanda #{order.id}</span>
              </div>
              <button onClick={() => setInvoicePreview(null)} className="rounded-md p-1 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 text-sm">
              <div className="mb-4 grid grid-cols-3 gap-3 text-xs">
                <div><p className="text-[10px] uppercase text-zinc-500">Serie</p><p className="font-mono text-zinc-100">{invoicePreview.series}</p></div>
                <div><p className="text-[10px] uppercase text-zinc-500">Gestiune</p><p className="text-zinc-100">{invoicePreview.management || "—"}</p></div>
                <div><p className="text-[10px] uppercase text-zinc-500">Monedă</p><p className="text-zinc-100">{invoicePreview.currency}</p></div>
              </div>
              <div className="mb-4 rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
                <p className="text-[10px] uppercase text-zinc-500 mb-1">Client</p>
                <p className="font-medium text-zinc-100">{invoicePreview.client?.name}</p>
                {invoicePreview.client?.cif && <p className="text-xs text-zinc-400">CIF: {invoicePreview.client.cif}</p>}
                <p className="text-xs text-zinc-400">{invoicePreview.client?.address}, {invoicePreview.client?.city}, {invoicePreview.client?.state}</p>
              </div>
              <table className="w-full text-xs">
                <thead className="bg-zinc-900/60 text-left text-[10px] uppercase text-zinc-500">
                  <tr><th className="px-2 py-2">Produs</th><th className="px-2 py-2 text-right">Cant.</th><th className="px-2 py-2 text-right">Preț</th><th className="px-2 py-2 text-right">TVA</th></tr>
                </thead>
                <tbody className="divide-y divide-zinc-800/60">
                  {(invoicePreview.products || []).map((p, i) => (
                    <tr key={i} className="text-zinc-200">
                      <td className="px-2 py-2">{p.name}{p.code && <span className="ml-2 font-mono text-[10px] text-zinc-500">{p.code}</span>}</td>
                      <td className="px-2 py-2 text-right">{p.quantity || "—"}</td>
                      <td className="px-2 py-2 text-right">{Number(p.price ?? 0).toFixed(2)}</td>
                      <td className="px-2 py-2 text-right">{p.vatPercentage != null ? `${p.vatPercentage}%` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-4 flex justify-end gap-4 border-t border-zinc-800 pt-3 text-xs">
                <div className="text-zinc-400">Net: <span className="font-medium text-zinc-200">{invoicePreview.totals?.net?.toFixed(2)} {invoicePreview.currency}</span></div>
                <div className="text-zinc-400">TVA: <span className="font-medium text-zinc-200">{invoicePreview.totals?.vat?.toFixed(2)} {invoicePreview.currency}</span></div>
                <div className="text-base font-semibold text-emerald-300">Total: {invoicePreview.totals?.gross?.toFixed(2)} {invoicePreview.currency}</div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-zinc-800 px-4 py-3">
              <button onClick={() => setInvoicePreview(null)} className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:bg-zinc-800">
                Renunță
              </button>
              <button data-testid="emit-invoice-btn" onClick={handleEmitInvoice} disabled={emitting}
                className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/50 bg-emerald-500/20 px-3 py-1.5 text-xs font-medium text-emerald-100 hover:bg-emerald-500/30 disabled:opacity-50">
                <FilePlus className="h-3.5 w-3.5" /> {emitting ? "Se emite…" : "Confirmă și emite factura"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel dialog */}
      {cancelOpen && (
        <div data-testid="cancel-dialog" className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4">
          <div className="w-full max-w-md rounded-xl border border-rose-500/30 bg-zinc-950 shadow-2xl">
            <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-3">
              <div className="flex items-center gap-2 text-rose-200">
                <Ban className="h-4 w-4" />
                <span className="text-sm font-semibold">Anulează comanda #{order.id}</span>
              </div>
              <button onClick={() => setCancelOpen(false)} className="rounded-md p-1 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3 p-4">
              <p className="text-xs text-zinc-400">
                {isGomag
                  ? "Comanda va fi marcată ca Anulată în Gomag. Introdu motivul (va fi salvat în notițele comenzii)."
                  : "Comanda va fi trimisă către eMAG cu statusul Anulată și codul motivului selectat."}
              </p>
              {isGomag ? (
                <div>
                  <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-zinc-500">Motiv anulare</label>
                  <textarea data-testid="cancel-reason-text" value={cancelText} onChange={(e) => setCancelText(e.target.value)} rows={3}
                    placeholder="Ex: clientul s-a răzgândit / stoc indisponibil"
                    className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:border-rose-500/50 focus:outline-none" />
                </div>
              ) : (
                <div>
                  <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-zinc-500">Motiv anulare (eMAG)</label>
                  <select data-testid="cancel-reason-select" value={cancelCode} onChange={(e) => setCancelCode(e.target.value)}
                    className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:border-rose-500/50 focus:outline-none">
                    {cancelReasons.map((r) => (
                      <option key={r.code} value={r.code} className="bg-zinc-900">
                        {r.code}. {r.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex items-center justify-end gap-2 pt-2">
                <button onClick={() => setCancelOpen(false)} className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:bg-zinc-800">
                  Renunță
                </button>
                <button data-testid="cancel-confirm-btn" onClick={handleCancelSubmit} disabled={cancelling}
                  className="inline-flex items-center gap-1.5 rounded-md border border-rose-500/50 bg-rose-500/20 px-3 py-1.5 text-xs font-medium text-rose-100 hover:bg-rose-500/30 disabled:opacity-50">
                  <Ban className="h-3.5 w-3.5" /> {cancelling ? "Se anulează…" : "Anulează comanda"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
